import {
    AfterViewInit, ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    effect,
    inject,
    Input,
    OnChanges, OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import interact from 'interactjs';
import { MatStepper } from "@angular/material/stepper";
import { SharedMaterialModule } from "@config/shared-material.module";
import { HttpService } from "@services/http.service";
import { Constants } from "@config/constants";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { IAppToken, ICreateSignRequestSigner, ISignRequestAndUploadRequest, ISignRequestAndUploadSignature } from '@modals/api.modal';
import { TableService } from '@services/table.service';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '@services/app.service';
import { ActivatedRoute } from '@angular/router';
import {
    EnumSignerRoleType,
    EnumSignTagTypes,
    FIELD_EDITOR_TOOLS,
    isFieldToolAllowedByActions,
    TFieldToolId,
} from '@modals/app.modal';
import { ToastService } from '@services/toast.service';
import { Messages } from '@config/messages';
import { PdfEditStateService } from '@services/pdf-edit-state.service';
import { MatDialog } from '@angular/material/dialog';
import { UploadSignComponent } from '@components/upload-sign/upload-sign.component';
import { SignTagElementComponent } from '@components/sign-tag-element/sign-tag-element.component';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { SignService } from '@services/sign.service';
import { CreateFlowStateService } from '@services/create-flow-state.service';
import {
    DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
    recolorSignatureImage,
} from '@utils/signature-color.util';

@Component({
    selector: 'app-pdf-view-edit',
    templateUrl: './pdf-view-edit.component.html',
    styleUrl: './pdf-view-edit.component.scss',
    imports: [SharedMaterialModule, MatCardModule, TranslateModule],
    standalone: true
})
export class PdfViewEditComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('pdfContainer') pdfContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('pdfPagesHost') pdfPagesHost!: ElementRef<HTMLDivElement>;
    @ViewChild('thumbnailsList') thumbnailsList?: ElementRef<HTMLDivElement>;
    @Output() previousStep = new EventEmitter<void>();
    @Input() pdfFile!: File | null;
    /** Custom document name for API payload; falls back to uploaded file name. */
    @Input() docName = '';
    @Input({ required: true }) appToken!: IAppToken;
    @Input() stepper!: MatStepper;
    @Output() onSubmitComplete = new EventEmitter<void>();
    @Output() selfSignComplete = new EventEmitter<{ signRequestId: string; docName: string }>();
    @Input() topPosition: number = 0;
    /** When true, user signs their own document via Sign/RequestAndUpload. */
    @Input() selfSignMode = false;
    /** Field step: keep tools panel fixed; only PDF area scrolls. */
    @Input() fixedToolsLayout = false;

    private _activatedRoute = inject(ActivatedRoute);
    private _toastr = inject(ToastService);
    private _dialog = inject(MatDialog);
    private _authService = inject(AuthService);
    private _signService = inject(SignService);
    private _createFlow = inject(CreateFlowStateService);
    pdfEditState = inject(PdfEditStateService);

    EnumSignerRoleType = EnumSignerRoleType;
    isPdfDragOver = false;
    draggingToolId: string | null = null;
    private _draggingTool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp" | null = null;
    private _dropPreviewEl: HTMLDivElement | null = null;
    private _dropPreviewPage: HTMLElement | null = null;
    /** Last dragover pointer — drop events from external tool tiles can report (0,0). */
    private _lastFieldDragPointer: { clientX: number; clientY: number } | null = null;
    private _toolDragMoved = false;
    annotations: any[] = [];
    pdfBase64: string | null = null;
    private _base64InFlight: Promise<string> | null = null;
    selectedAnnotation: any = null;

   
    private _justAddedByClick = false;
    selectedFontSize: number = 16;
    placeholder: string = '';
    fontSizes: number[] = [8, 10, 12, 14, 16, 18, 20, 24];
    totalPages: number[] = [];
    pageThumbnails: string[] = [];
    currentPageIndex = 1;
    pdfZoomPercent = 100;
    private readonly minPdfZoom = 50;
    private readonly maxPdfZoom = 200;
    private readonly pdfZoomStep = 25;
    private readonly pdfPageTopInset = 8;
    private _pdfScrollHandler = () => this.syncCurrentPageFromScroll();
    private _pdfScrollListenerAttached = false;
    isDraftRequest: boolean = false;
    private _renderSeq = 0;
    private _renderInFlight: Promise<void> | null = null;
    private _pendingEnsure = false;
    private _pendingForceEnsure = false;
    private _lastRenderedFileKey: string | null = null;
    private _lastRenderedWidth = 0;
    private _outsideClickHandler = (e: MouseEvent) => this.handleOutsideClick(e);

    /** Cached signature for self-sign — optional preview via top bar; fields sign on click. */
    private _selfSignActiveSignature: {
        imageBase64: string;
        sourceImageBase64?: string;
        signatureColor?: string;
        signatureId?: string;
        signComment?: string;
        comment?: string;
    } | null = null;

    private getSelfSignFieldLabel(type: string): string {
        if (!this.selfSignMode) {
            const labels: Record<string, string> = {
                esign: 'Signature',
                initials: 'Initial',
                comment: 'Comment',
                stamp: 'Stamp',
                text: 'Editable Text',
            };
            return this.placeholder || labels[type] || '';
        }
        if (type === 'esign') return 'Click to sign';
        if (type === 'initials') return this.resolveSelfSignUserName();
        if (type === 'text') return 'Enter text here';
        if (type === 'comment') return 'Click to add comment';
        if (type === 'stamp') return 'Click to add stamp';
        if (type === 'checkbox') return 'Check to confirm';
        return '';
    }

    private resolveSelfSignUserName(): string {
        return (this.tableService.tableInputData?.[0]?.name ?? '').toString().trim();
    }

    private getTodayDateIso(): string {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private getSelfSignCommentLabel(): string {
        return Constants.DEFAULT_COMMENT_PREFIX.replace('{ROLE}', 'Signer');
    }

    private getSelfSignCanonicalWrapperSize(
        type: string,
    ): { width: number; height: number } | null {
        if (!this.selfSignMode) return null;
        switch (type) {
            case 'text':
            case 'initials':
                return {
                    width: Constants.CANONICAL_TAG_FIELD_WIDTH_PX,
                    height: Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
                };
            case 'date':
                return {
                    width: Constants.CANONICAL_TAG_DATE_WIDTH_PX,
                    height: Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
                };
            case 'checkbox':
                return {
                    width: this._signService.getSelfSignCheckboxHostWidth(
                        this.getSelfSignSignTagLabel('checkbox'),
                    ),
                    height: Constants.CANONICAL_TAG_CHECKBOX_HEIGHT_PX,
                };
            case 'comment':
                return {
                    width: Constants.DEFAULT_SIGN_COMMENT_WIDTH,
                    height: Constants.DEFAULT_SIGN_COMMENT_HEIGHT + 24,
                };
            default:
                return null;
        }
    }

    private applySelfSignLabeledFieldClasses(_wrapper: HTMLElement, _type: string): void {
        /* Legacy hook — self-sign labeled fields use SignTagElementComponent. */
    }

    private mapSelfSignTypeToEnum(type: string): EnumSignTagTypes {
        switch (type) {
            case 'text':
                return EnumSignTagTypes.TEXT;
            case 'initials':
                return EnumSignTagTypes.INITIALS;
            case 'date':
                return EnumSignTagTypes.DATE;
            case 'checkbox':
                return EnumSignTagTypes.CHECKBOX;
            default:
                return EnumSignTagTypes.TEXT;
        }
    }

    private getSelfSignSignTagLabel(type: string): string {
        switch (type) {
            case 'text':
                return 'Text';
            case 'initials':
                return 'Initials';
            case 'date':
                return 'Date';
            case 'checkbox':
                return 'Check to confirm';
            default:
                return '';
        }
    }

    private isSelfSignSignTagHost(wrapper: HTMLElement): boolean {
        return wrapper.classList.contains('self-sign-tag-host');
    }

    private isMobileSelfSignLayout(): boolean {
        return typeof window !== 'undefined' && window.innerWidth <= 768;
    }

    private getSelfSignFieldScaleFactor(
        type: string,
        width: number,
        height: number,
    ): number {
        const canonical = this.getSelfSignCanonicalWrapperSize(type);
        if (!canonical?.width || !canonical?.height || width <= 0 || height <= 0) {
            return 1;
        }
        if (
            (type === 'text' || type === 'initials' || type === 'date' || type === 'checkbox') &&
            width >= canonical.width * 0.92 &&
            height >= canonical.height * 0.92
        ) {
            return 1;
        }
        return Math.max(0.5, Math.min(width / canonical.width, height / canonical.height));
    }

    private applySelfSignFieldScale(
        wrapper: HTMLElement,
        type: string,
        width: number,
        height: number,
    ): void {
        const scale = this.getSelfSignFieldScaleFactor(type, width, height);
        wrapper.style.setProperty('--scale-factor', scale.toFixed(3));
    }

    private attachSelfSignTagHostResizeSync(
        host: HTMLElement,
        type: 'text' | 'initials' | 'date' | 'checkbox',
        initialWidth: number,
        initialHeight: number,
    ): void {
        const resizable = host.querySelector('.resizable') as HTMLElement | null;
        if (!resizable || typeof ResizeObserver === 'undefined') return;

        const syncHost = () => {
            const measuredW = Math.round(resizable.offsetWidth);
            const measuredH = Math.round(resizable.offsetHeight);
            if (measuredW <= 0 || measuredH <= 0) return;

            let w = Math.max(measuredW, initialWidth);
            const h = Math.max(measuredH, initialHeight);
            if (type === 'checkbox') {
                const label = this.readSelfSignCheckboxLabel(host);
                w = Math.max(w, this._signService.getSelfSignCheckboxHostWidth(label));
            }
            host.style.width = `${w}px`;
            host.style.height = `${h}px`;
            host.style.maxWidth = `${w}px`;
            if (type === 'checkbox') {
                resizable.style.width = `${w}px`;
            }
            this.applySelfSignFieldScale(host, type, w, h);
        };

        const observer = new ResizeObserver(() => syncHost());
        observer.observe(resizable);
        syncHost();
    }

    private isSelfSignLabeledCanonicalField(type: string): boolean {
        return type === 'text' || type === 'initials' || type === 'date' || type === 'checkbox';
    }

    private getSelfSignCommentPlaceholderSize(
        pageEl: HTMLElement | null,
    ): { width: number; height: number } {
        const canonical = {
            width: Constants.CANONICAL_TAG_FIELD_WIDTH_PX,
            height: Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
        };
        if (!pageEl) return canonical;

        const frame = this.getPageTagCoordinateFrame(pageEl);
        const pageMargin = 8;
        const maxWidth = Math.max(frame.refWidth - pageMargin, 64);
        let width = Math.min(canonical.width, maxWidth);
        let height = canonical.height;

        if (width < canonical.width) {
            const scale = width / canonical.width;
            height = Math.max(Math.round(canonical.height * scale), 26);
        }

        return { width, height };
    }

    private getSelfSignFilledCommentSize(
        pageEl: HTMLElement | null,
    ): { width: number; height: number } {
        const canonical = {
            width: Constants.DEFAULT_SIGN_COMMENT_WIDTH,
            height: Constants.DEFAULT_SIGN_COMMENT_HEIGHT + 24,
        };
        if (!pageEl) return canonical;

        const frame = this.getPageTagCoordinateFrame(pageEl);
        const refW = frame.refWidth;
        const refH = frame.refHeight;
        const pageMargin = 8;
        const maxWidth = Math.max(refW - pageMargin, 64);

        const widthPct = this.isMobileSelfSignLayout()
            ? 55
            : Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE;
        const heightPct = this.isMobileSelfSignLayout()
            ? 8
            : Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE;
        const width = Math.min(
            Math.max(Math.round((widthPct / 100) * refW), Constants.MINIMUM_SIGN_COMMENT_WIDTH),
            maxWidth,
        );
        const height = Math.max(Math.round((heightPct / 100) * refH), 56);

        return { width, height };
    }

    private applySelfSignFilledCommentDimensions(wrapper: HTMLElement): void {
        const pageElement = wrapper.closest('.pdf-page') as HTMLElement | null;
        if (!pageElement) return;

        const labelEl = wrapper.querySelector('.self-sign-comment-field__label') as HTMLElement | null;
        const bodyEl = wrapper.querySelector('.self-sign-comment-field__body') as HTMLElement | null;
        const suffixEl = wrapper.querySelector('.self-sign-comment-field__suffix') as HTMLElement | null;

        const label = (labelEl?.textContent ?? this.getSelfSignCommentLabel()).trim();
        const body = (bodyEl?.textContent ?? wrapper.dataset['commentText'] ?? '').trim();
        const suffix = (suffixEl?.textContent ?? Constants.DEFAULT_COMMENT_SUFFIX).trim();

        const frame = this.getPageTagCoordinateFrame(pageElement);
        const pageMargin = 8;
        const maxWidth = Math.max(frame.refWidth - pageMargin, Constants.MINIMUM_SIGN_COMMENT_WIDTH);

        let width = Math.min(
            this._signService.getSelfSignCommentHostWidth(label, body, suffix),
            maxWidth,
        );
        const height = this.getSelfSignFilledCommentSize(pageElement).height;

        const tx = parseFloat(wrapper.getAttribute('data-x') || '0') || 0;
        const ty = parseFloat(wrapper.getAttribute('data-y') || '0') || 0;
        const left = (parseFloat(wrapper.style.left) || 0) + tx;
        const top = (parseFloat(wrapper.style.top) || 0) + ty;
        const clamped = this.clampSelfSignFieldRect(
            left,
            top,
            width,
            height,
            pageElement,
        );

        wrapper.style.width = `${clamped.width}px`;
        wrapper.style.height = `${clamped.height}px`;
        wrapper.style.maxWidth = `${clamped.width}px`;
        wrapper.style.left = `${clamped.x}px`;
        wrapper.style.top = `${clamped.y}px`;
        wrapper.style.transform = 'translate(0px, 0px)';
        wrapper.setAttribute('data-x', '0');
        wrapper.setAttribute('data-y', '0');
    }

    private getSelfSignResponsiveFieldSize(
        type: string,
        pageEl: HTMLElement | null,
    ): { width: number; height: number } {
        const canonical =
            this.getSelfSignCanonicalWrapperSize(type) ??
            PdfViewEditComponent.getDefaultWrapperSize(type);

        if (!pageEl) return canonical;

        const frame = this.getPageTagCoordinateFrame(pageEl);
        const refW = frame.refWidth;
        const refH = frame.refHeight;
        const pageMargin = 8;
        const maxWidth = Math.max(refW - pageMargin, 64);

        let width: number;
        let height: number;

        if (type === 'comment') {
            return this.getSelfSignCommentPlaceholderSize(pageEl);
        }

        if (this.isSelfSignLabeledCanonicalField(type)) {
            width = canonical.width;
            height = canonical.height;
            width = Math.min(width, maxWidth);
            if (width < canonical.width) {
                const scale = width / canonical.width;
                height = Math.max(Math.round(canonical.height * scale), 26);
            }
            height = Math.min(height, Math.max(frame.refHeight - pageMargin, 26));
            return { width, height };
        }

        width = Math.min(canonical.width, maxWidth);
        height = canonical.height;
        if (width < canonical.width) {
            const scale = width / canonical.width;
            height = Math.max(Math.round(canonical.height * scale), 26);
        }

        return { width, height };
    }

    private clampSelfSignFieldRect(
        x: number,
        y: number,
        width: number,
        height: number,
        pageEl: HTMLElement,
    ): { x: number; y: number; width: number; height: number } {
        const frame = this.getPageTagCoordinateFrame(pageEl);
        const maxW = Math.max(frame.refWidth - 4, 72);
        const maxH = Math.max(frame.refHeight - 4, 24);
        const w = Math.min(Math.max(width, 72), maxW);
        const h = Math.min(Math.max(height, 24), maxH);
        const maxX = Math.max(0, frame.refWidth - w);
        const maxY = Math.max(0, frame.refHeight - h);
        return {
            x: Math.min(Math.max(0, x), maxX),
            y: Math.min(Math.max(0, y), maxY),
            width: w,
            height: h,
        };
    }

    private addSelfSignSignTagField(
        type: 'text' | 'initials' | 'date' | 'checkbox',
        x: number,
        y: number,
        page?: number,
        dimensions?: { width: number; height: number },
    ): void {
        const pageElement = this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')[(page || 1) - 1] as
            | HTMLElement
            | undefined;
        const responsiveSize = this.getSelfSignResponsiveFieldSize(type, pageElement ?? null);
        const defaultWrapperSize = PdfViewEditComponent.getDefaultWrapperSize(type);
        let wrapperWidth = dimensions?.width ?? responsiveSize.width ?? defaultWrapperSize.width;
        let wrapperHeight = dimensions?.height ?? responsiveSize.height ?? defaultWrapperSize.height;
        let posX = x;
        let posY = y;

        if (pageElement) {
            const clamped = this.clampSelfSignFieldRect(posX, posY, wrapperWidth, wrapperHeight, pageElement);
            posX = clamped.x;
            posY = clamped.y;
            wrapperWidth = clamped.width;
            wrapperHeight = clamped.height;
        }

        const host = document.createElement('div');
        host.className = 'sign-tag-element-container self-sign-tag-host';
        host.dataset['annotationType'] = type;
        host.dataset['baseFontSize'] = String(
            type === 'initials' ? (this.selectedFontSize || 16) : (this.selectedFontSize || 14),
        );
        host.setAttribute('data-tag-type', type.toUpperCase());
        host.style.position = 'absolute';
        host.style.left = `${posX}px`;
        host.style.top = `${posY}px`;
        host.style.width = `${wrapperWidth}px`;
        host.style.height = `${wrapperHeight}px`;
        host.style.maxWidth = `${wrapperWidth}px`;
        host.style.boxSizing = 'border-box';
        host.style.overflow = 'visible';
        host.style.zIndex = '25';
        host.style.pointerEvents = 'auto';
        this.applySelfSignFieldScale(host, type, wrapperWidth, wrapperHeight);

        const enumType = this.mapSelfSignTypeToEnum(type);
        const label = this.getSelfSignSignTagLabel(type);
        const renderOptions: { initialTagValue?: string; initialDate?: Date } = {};
        if (type === 'initials') {
            renderOptions.initialTagValue = this.resolveSelfSignUserName();
        } else if (type === 'date') {
            renderOptions.initialDate = new Date(`${this.getTodayDateIso()}T12:00:00`);
        }

        const onRemove = () => {
            host.remove();
            const idx = this.annotations.findIndex((a) => a.element === host);
            if (idx !== -1) {
                this.annotations.splice(idx, 1);
            }
            if (this.selectedAnnotation?.element === host) {
                this.selectedAnnotation = null;
                this.pdfEditState.setSelectedAnnotation(null);
                this.cdr.detectChanges();
            }
        };

        this._signService.renderSelfSignTagElement(host, label, enumType, onRemove, renderOptions);
        this.attachSelfSignTagHostResizeSync(host, type, wrapperWidth, wrapperHeight);
        this.attachSelfSignSignTagInputWidthSync(host);

        if (type === 'checkbox') {
            const syncCheckboxWidth = () => this.syncSelfSignSignTagHostWidth(host);
            syncCheckboxWidth();
            requestAnimationFrame(() => syncCheckboxWidth());
            setTimeout(syncCheckboxWidth, 0);
            setTimeout(syncCheckboxWidth, 150);
        }

        host.addEventListener('click', (event) => {
            event.stopPropagation();
            this.selectAnnotation(host, type);
        });

        const fieldHost =
            (pageElement?.querySelector('.textLayer') as HTMLElement | null) ?? pageElement;
        if (fieldHost) {
            fieldHost.appendChild(host);
        }

        const pageNumber = page || this.getPageNumberForAnnotation(host) || 1;
        const defaultProperties = {
            placeholder: type === 'checkbox' ? this.getSelfSignSignTagLabel('checkbox') : '',
            fontSize: type === 'initials' ? 16 : 14,
            user: this.resolveDefaultAssigneeEmail(),
            page: pageNumber,
            ...(type === 'text' || type === 'initials'
                ? { signatureColor: DEFAULT_SELF_SIGN_SIGNATURE_COLOR }
                : {}),
        };

        if (type === 'text' || type === 'initials') {
            host.dataset['fieldColor'] = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
        }

        const annotation = {
            element: host,
            type,
            properties: { ...defaultProperties },
        };

        this.annotations.push(annotation);
        this.selectAnnotation(host, type);
    }

    private getSelfSignCommentPlaceholder(): string {
        return this.getSelfSignFieldLabel('comment');
    }

    private renderSelfSignCommentBox(
        wrapper: HTMLElement,
        commentText: string,
        imageBase64: string,
        fillColor: string,
    ): void {
        wrapper.dataset['selfSignSigned'] = 'true';
        wrapper.classList.remove('annotation-wrapper--self-sign-target');
        wrapper.classList.remove('annotation-wrapper--self-sign-comment-placeholder');
        wrapper.classList.add(
            'annotation-wrapper--self-sign-comment-filled',
            'annotation-wrapper--signed',
        );
        wrapper.style.cursor = 'pointer';
        wrapper.style.border = 'none';
        wrapper.style.backgroundColor = 'transparent';
        wrapper.style.padding = '0';
        wrapper.style.boxShadow = 'none';

        const content = this.getAnnotationContentElement(wrapper);
        if (!content) return;

        const label = this.getSelfSignCommentLabel();

        content.className = 'annotation-content self-sign-comment-field';
        content.innerHTML = '';
        content.style.padding = '0';
        content.style.margin = '0';
        content.style.overflow = 'visible';
        content.style.fontStyle = 'normal';
        content.style.lineHeight = 'normal';
        content.style.width = '100%';
        content.style.height = '100%';
        content.style.display = 'block';
        content.style.background = 'transparent';
        content.style.textAlign = 'left';

        const hiddenImg = document.createElement('img');
        hiddenImg.src = imageBase64;
        hiddenImg.alt = '';
        hiddenImg.style.display = 'none';
        hiddenImg.setAttribute('data-type', 'COMMENT');
        hiddenImg.setAttribute('data-value-available', 'true');
        hiddenImg.setAttribute('data-value', commentText);
        content.appendChild(hiddenImg);

        const border = document.createElement('div');
        border.className = 'self-sign-comment-field__border';

        const labelEl = document.createElement('span');
        labelEl.className = 'self-sign-comment-field__label';
        labelEl.textContent = label;

        const body = document.createElement('div');
        body.className = 'self-sign-comment-field__body';
        body.textContent = commentText;
        body.style.color = fillColor || DEFAULT_SELF_SIGN_SIGNATURE_COLOR;

        const suffixEl = document.createElement('span');
        suffixEl.className = 'self-sign-comment-field__suffix';
        suffixEl.textContent = Constants.DEFAULT_COMMENT_SUFFIX;

        border.appendChild(labelEl);
        border.appendChild(body);
        border.appendChild(suffixEl);
        content.appendChild(border);

        wrapper.dataset['commentText'] = commentText;
        this.applySelfSignFilledCommentDimensions(wrapper);
        this.syncAnnotationWrapperSize(wrapper);
    }

    get selfSignActivePreview(): string | null {
        return this._selfSignActiveSignature?.imageBase64 ?? null;
    }

    get hasSelfSignActiveSignature(): boolean {
        return !!(this._selfSignActiveSignature?.imageBase64 ?? '').trim();
    }

    changeSelfSignSignature(): void {
        void this.openSelfSignPickerDialog();
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private httpService: HttpService,
        public tableService: TableService,
        private _appService: AppService
    ) {
        effect(() => {
            this.pdfEditState.selectedTool();
            this.updateCursor();
        });
    }

    private updateCursor(): void {
        if (!this.pdfContainer?.nativeElement) return;
        const tool = this.pdfEditState.selectedTool();
        this.pdfContainer.nativeElement.style.cursor = tool ? 'crosshair' : 'default';
    }

    ngOnInit(): void {
        pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs';
        this.isDraftRequest = this._activatedRoute.snapshot.data["isDraftRequest"] ?? false;
    }

    ngOnChanges(): void {
        if (this.pdfFile) {
            // New file: reset base64 cache so submit uses correct content
            this.pdfBase64 = null;
            this._base64InFlight = null;
            this.currentPageIndex = 1;
            this.pdfZoomPercent = 100;
            // console.log('[PdfViewEdit] ngOnChanges – pdfFile:', this.pdfFile?.name, 'size:', this.pdfFile?.size, 'hasContainer:', !!this.pdfContainer?.nativeElement);
            this.convertPdfToBase64();
            this._scheduleRender();
        }
    }

    ngAfterViewInit() {
        document.addEventListener('click', this._outsideClickHandler);
        this.pdfEditState.registerAnnotationHandlers({
            updateProperty: (prop, val) => this.updateAnnotationProperty(prop, val),
            copyToPages: (opt) => this.copyAnnotationToPages(opt)
        });
        if (this.fixedToolsLayout) {
            this.pdfEditState.setSelectedTool(null);
        }
        this._scheduleRender();
    }

    private _scheduleRender(): void {
        if (!this.pdfFile) return;
        if (this.pdfContainer?.nativeElement && this.pdfPagesHost?.nativeElement) {
            this.ensurePdfRendered(false);
        } else {
            setTimeout(() => this._scheduleRender(), 50);
        }
    }

    ensurePdfRendered(force: boolean = false): void {
        if (!this.pdfFile || !this.pdfContainer?.nativeElement || !this.pdfPagesHost?.nativeElement) {
            if (this.pdfFile && this.selfSignMode) {
                requestAnimationFrame(() => this.ensurePdfRendered(force));
            }
            return;
        }
        const container = this.pdfContainer.nativeElement;
        const pagesRoot = this.pdfPagesHost.nativeElement;
        const width = container.clientWidth;
        if (width <= 0) {
            requestAnimationFrame(() => this.ensurePdfRendered(force));
            return;
        }

        const key = this._fileKey(this.pdfFile);
        const hasPages = !!pagesRoot.querySelector('.pdf-page');
        const widthStable = Math.abs(width - this._lastRenderedWidth) < 2;
        if (!force && hasPages && this._lastRenderedFileKey === key && widthStable) return;

        this.convertPdfToBase64();

        if (this._renderInFlight) {
            this._pendingEnsure = true;
            this._pendingForceEnsure = this._pendingForceEnsure || force;
            return;
        }

        this._renderInFlight = this.renderPDF(this.pdfFile, key).finally(() => {
            this._renderInFlight = null;
            if (this._pendingEnsure) {
                const pendingForce = this._pendingForceEnsure;
                this._pendingEnsure = false;
                this._pendingForceEnsure = false;
                this.ensurePdfRendered(pendingForce);
            }
        });
    }

    private _fileKey(file: File): string {
        return `${file.name}::${file.size}::${file.lastModified}`;
    }

    ngOnDestroy() {
        document.removeEventListener('click', this._outsideClickHandler);
        this.detachPdfScrollListener();
        this.removeDropPreview();
        this.pdfEditState.unregisterAnnotationHandlers();
        this.pdfEditState.setSelectedAnnotation(null);
    }

    toggleTool(tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp"): void {
        this.pdfEditState.toggleTool(tool);
    }

    onToolClick(
        tool: TFieldToolId,
        event: Event
    ): void {
        if (this._toolDragMoved) {
            event.preventDefault();
            event.stopPropagation();
            this._toolDragMoved = false;
            return;
        }
        if (this.isToolDisabled(tool)) return;
        this.toggleTool(tool);
    }

    isAllReviewers() {
        return this.tableService.tableInputData.length > 0 &&
            this.tableService.tableInputData.every(
                (data) =>
                    (data.role ?? "").toString().trim().toLowerCase() ===
                    EnumSignerRoleType.REVIEWER.toLowerCase()
            );
    }

    isToolDisabled(tool: TFieldToolId | { id: TFieldToolId; requiresSigner: boolean }): boolean {
        const toolId = typeof tool === 'string' ? tool : tool.id;
        const requiresSigner = typeof tool === 'string'
            ? this.fieldTools.find((item) => item.id === toolId)?.requiresSigner ?? true
            : tool.requiresSigner;
        if (requiresSigner && this.isAllReviewers()) {
            return true;
        }
        return !isFieldToolAllowedByActions(toolId, this.tableService.tableInputData);
    }

    readonly fieldTools = FIELD_EDITOR_TOOLS;

    get canGoToPreviousPage(): boolean {
        return this.currentPageIndex > 1;
    }

    get canGoToNextPage(): boolean {
        return this.currentPageIndex < this.totalPages.length;
    }

    get canZoomOut(): boolean {
        return this.pdfZoomPercent > this.minPdfZoom;
    }

    get canZoomIn(): boolean {
        return this.pdfZoomPercent < this.maxPdfZoom;
    }

    goToPreviousPage(): void {
        if (!this.canGoToPreviousPage) return;
        this.scrollToPage(this.currentPageIndex - 1);
    }

    goToNextPage(): void {
        if (!this.canGoToNextPage) return;
        this.scrollToPage(this.currentPageIndex + 1);
    }

    zoomOut(): void {
        if (!this.canZoomOut) return;
        this.pdfZoomPercent = Math.max(this.minPdfZoom, this.pdfZoomPercent - this.pdfZoomStep);
        this.applyPdfZoom();
    }

    zoomIn(): void {
        if (!this.canZoomIn) return;
        this.pdfZoomPercent = Math.min(this.maxPdfZoom, this.pdfZoomPercent + this.pdfZoomStep);
        this.applyPdfZoom();
    }

    scrollToPage(pageNumber: number): void {
        const container = this.pdfContainer?.nativeElement;
        if (!container) return;

        const pageEl = container.querySelectorAll('.pdf-page')[pageNumber - 1] as HTMLElement | undefined;
        if (!pageEl) return;

        const containerTop = container.getBoundingClientRect().top;
        const pageTop = pageEl.getBoundingClientRect().top;
        const nextScrollTop = container.scrollTop + (pageTop - containerTop) - this.pdfPageTopInset;

        container.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'smooth' });
        this.currentPageIndex = pageNumber;
        this.cdr.markForCheck();
        this.queueActiveThumbnailScroll(pageNumber);
    }

    private queueActiveThumbnailScroll(pageNumber: number): void {
        if (!this.fixedToolsLayout) return;
        requestAnimationFrame(() => {
            this.scrollActiveThumbnailIntoView(pageNumber);
        });
    }

    private scrollActiveThumbnailIntoView(pageNumber: number): void {
        const list = this.thumbnailsList?.nativeElement;
        if (!list) return;

        const thumb = list.querySelector(
            `[data-thumbnail-page="${pageNumber}"]`,
        ) as HTMLElement | null;
        if (!thumb) return;

        const listRect = list.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        const isVisible =
            thumbRect.top >= listRect.top &&
            thumbRect.bottom <= listRect.bottom;

        if (!isVisible) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    private applyPdfZoom(): void {
        const inner = this.pdfPagesHost?.nativeElement;
        if (!inner) return;
        inner.style.zoom = `${this.pdfZoomPercent / 100}`;
        this.cdr.markForCheck();
    }

    private syncCurrentPageFromScroll(): void {
        const container = this.pdfContainer?.nativeElement;
        if (!container) return;

        const pages = Array.from(container.querySelectorAll('.pdf-page')) as HTMLElement[];
        if (!pages.length) return;

        const scrollTop = container.scrollTop;
        let activePage = 1;
        const pageDetectOffset = this.pdfPageTopInset + 12;

        for (let i = 0; i < pages.length; i++) {
            if (pages[i].offsetTop <= scrollTop + pageDetectOffset) {
                activePage = i + 1;
            }
        }

        if (this.currentPageIndex !== activePage) {
            this.currentPageIndex = activePage;
            this.cdr.markForCheck();
            this.queueActiveThumbnailScroll(activePage);
        }
    }

    private attachPdfScrollListener(): void {
        const container = this.pdfContainer?.nativeElement;
        if (!container || this._pdfScrollListenerAttached) return;
        container.addEventListener('scroll', this._pdfScrollHandler, { passive: true });
        this._pdfScrollListenerAttached = true;
    }

    private detachPdfScrollListener(): void {
        const container = this.pdfContainer?.nativeElement;
        if (!container || !this._pdfScrollListenerAttached) return;
        container.removeEventListener('scroll', this._pdfScrollHandler);
        this._pdfScrollListenerAttached = false;
    }

    handleOutsideClick(event: MouseEvent): void {
        if (this._justAddedByClick) {
            this._justAddedByClick = false;
            return;
        }

        const clickedElement = event.target as HTMLElement;
        const sigPropsPanel = document.querySelector('.signature-properties-panel-outside');
        const matSelectOverlay = document.querySelector('.cdk-overlay-container');
        const createPropsPanel = document.querySelector('.create-sign-stepper__props-wrapper');

        if (
            createPropsPanel?.contains(clickedElement) ||
            sigPropsPanel?.contains(clickedElement) ||
            matSelectOverlay?.contains(clickedElement)
        ) {
            return;
        }

        const isMobilePropertyDrawer = typeof window !== 'undefined'
            && window.innerWidth <= 1024
            && this.pdfEditState.propertyDrawerOpen();

        if (
            !this.isClickOnAnnotation(clickedElement)
        ) {
            const isToolTile = !!clickedElement.closest('.field-editor__tool');
            const isPdfArea = !!clickedElement.closest('.pdf-container');
            if (!isToolTile && !isPdfArea) {
                this.pdfEditState.setSelectedTool(null);
            }

            if (isMobilePropertyDrawer) {
                return;
            }

            if (this.selectedAnnotation) {
                this.setAnnotationSelected(this.selectedAnnotation.element as HTMLElement, false);
            }
            this.selectedAnnotation = null;
            this.pdfEditState.setSelectedAnnotation(null);
        }
    }

    private isClickOnAnnotation(element: HTMLElement): boolean {
        // Check if the clicked element is an annotation or inside an annotation.
        return this.annotations.some((annotation) => annotation.element.contains(element));
    }

    handlePdfClick(event: MouseEvent): void {
        const tool = this.pdfEditState.selectedTool();
        if (!tool || this.isToolDisabled(tool)) {
            return;
        }

        const placement = this.getFieldPlacementFromClient(event.clientX, event.clientY, tool);
        if (!placement) return;

        this._justAddedByClick = true;
        this.addAnnotation(tool, placement.x, placement.y, placement.page);
        this.pdfEditState.setSelectedTool(null);
    }

    getAnnotationCoordinates(event: MouseEvent): { x: number; y: number; page: number } | null {
        return this.getCoordinatesFromClient(event.clientX, event.clientY);
    }

    /**
     * Use the page canvas as the coordinate frame — matches ngx-extended-pdf-viewer where tags are
     * positioned as % of `.textLayer` laid over the same bitmap (avoids editor vs mail-link drift).
     */
    private getPdfPageCanvas(pageEl: HTMLElement | null): HTMLCanvasElement | null {
        if (!pageEl) return null;
        return pageEl.querySelector('canvas');
    }

    private getPageReferenceRect(pageEl: HTMLElement): DOMRect {
        return this._signService.getPageTagCoordinateFrame(pageEl).refRect;
    }

    private getCoordinatesFromClient(clientX: number, clientY: number): { x: number; y: number; page: number } | null {
        const pdfPages = Array.from(this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')) as HTMLElement[];

        for (let i = 0; i < pdfPages.length; i++) {
            const pageEl = pdfPages[i];
            const frame = this.getPageTagCoordinateFrame(pageEl);
            const refRect = frame.refRect;
            if (frame.refWidth < 1 || frame.refHeight < 1) continue;
            if (
                clientY >= refRect.top &&
                clientY <= refRect.bottom &&
                clientX >= refRect.left &&
                clientX <= refRect.right
            ) {
                const local = this.clientToPageLocal(clientX, clientY, pageEl);
                return { x: local.x, y: local.y, page: i + 1 };
            }
        }

        return null;
    }

    /** Signature footprint — all field types use the same default inner size. */
    private static readonly SIGNATURE_FIELD_INNER = { width: 136, height: 28 };

    private static readonly DEFAULT_FIELD_INNER: Record<string, { width: number; height: number }> = {
        esign: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        initials: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        stamp: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        text: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        date: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        checkbox: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
        comment: PdfViewEditComponent.SIGNATURE_FIELD_INNER,
    };

    
    private static readonly WRAPPER_CHROME = 12;

    private static getDefaultWrapperSize(type: string): { width: number; height: number } {
        const inner = PdfViewEditComponent.DEFAULT_FIELD_INNER[type] ?? PdfViewEditComponent.DEFAULT_FIELD_INNER['text'];
        return {
            width: inner.width + PdfViewEditComponent.WRAPPER_CHROME,
            height: inner.height + PdfViewEditComponent.WRAPPER_CHROME,
        };
    }

    private static readonly TOOL_DIMENSIONS: Record<string, { width: number; height: number; label: string }> = {
        esign: { ...PdfViewEditComponent.getDefaultWrapperSize('esign'), label: 'Signature' },
        initials: { ...PdfViewEditComponent.getDefaultWrapperSize('initials'), label: 'Initials' },
        stamp: { ...PdfViewEditComponent.getDefaultWrapperSize('stamp'), label: 'Stamp' },
        text: { ...PdfViewEditComponent.getDefaultWrapperSize('text'), label: 'Text' },
        date: { ...PdfViewEditComponent.getDefaultWrapperSize('date'), label: 'Date' },
        checkbox: { ...PdfViewEditComponent.getDefaultWrapperSize('checkbox'), label: 'Checkbox' },
        comment: { ...PdfViewEditComponent.getDefaultWrapperSize('comment'), label: 'Comment' },
    };

    private static readonly TOOL_LABELS: Record<string, string> = {
        esign: 'Signature',
        initials: 'Initials',
        stamp: 'Stamp',
        text: 'Text',
        date: 'Date',
        checkbox: 'Checkbox',
        comment: 'Comment',
    };

    private static readonly TOOL_ICONS: Record<string, string> = {
        esign: 'edit', initials: 'short_text', stamp: 'image',
        text: 'text_fields', date: 'calendar_today', checkbox: 'check_box', comment: 'comment'
    };

    private getPdfZoomScale(): number {
        return Math.max(this.pdfZoomPercent ?? 100, 10) / 100;
    }

    /** Bake translate into left/top so fields move smoothly anywhere on the page. */
    private commitAnnotationDragPosition(wrapper: HTMLElement): void {
        const tx = parseFloat(wrapper.getAttribute('data-x') || '0') || 0;
        const ty = parseFloat(wrapper.getAttribute('data-y') || '0') || 0;
        if (!tx && !ty) return;

        const left = (parseFloat(wrapper.style.left || '0') || 0) + tx;
        const top = (parseFloat(wrapper.style.top || '0') || 0) + ty;
        wrapper.style.left = `${left}px`;
        wrapper.style.top = `${top}px`;
        wrapper.style.transform = 'translate3d(0px, 0px, 0)';
        wrapper.setAttribute('data-x', '0');
        wrapper.setAttribute('data-y', '0');
    }

    private attachAnnotationFieldHoverMove(
        wrapper: HTMLElement,
        moveIcon: HTMLElement,
        type: string,
    ): void {
        const iconSize = 28;
        const edgePad = 6;
        const resizeEdgeMargin = 10;

        const isInResizeZone = (clientX: number, clientY: number): boolean => {
            const rect = wrapper.getBoundingClientRect();
            const zoom = this.getPdfZoomScale();
            const x = (clientX - rect.left) / zoom;
            const y = (clientY - rect.top) / zoom;
            const w = wrapper.offsetWidth;
            const h = wrapper.offsetHeight;
            return (
                x < resizeEdgeMargin ||
                y < resizeEdgeMargin ||
                x > w - resizeEdgeMargin ||
                y > h - resizeEdgeMargin
            );
        };

        const setHoverMode = (mode: 'none' | 'move' | 'resize'): void => {
            wrapper.classList.toggle('annotation-wrapper--move-hover', mode === 'move');
            wrapper.classList.toggle('annotation-wrapper--resize-hover', mode === 'resize');
        };

        const positionIcon = (clientX: number, clientY: number): void => {
            const rect = wrapper.getBoundingClientRect();
            const zoom = this.getPdfZoomScale();
            const localX = (clientX - rect.left) / zoom;
            const localY = (clientY - rect.top) / zoom;
            const half = iconSize / 2;
            const maxX = wrapper.offsetWidth - edgePad - half;
            const maxY = wrapper.offsetHeight - edgePad - half;
            const min = edgePad + half;
            const x = Math.max(min, Math.min(localX, maxX));
            const y = Math.max(min, Math.min(localY, maxY));
            moveIcon.style.left = `${x}px`;
            moveIcon.style.top = `${y}px`;
        };

        const showIconAt = (clientX: number, clientY: number): void => {
            positionIcon(clientX, clientY);
            moveIcon.classList.add('annotation-move-icon--visible');
        };

        const hideIcon = (): void => {
            if (!wrapper.classList.contains('is-dragging')) {
                moveIcon.classList.remove('annotation-move-icon--visible');
                moveIcon.style.left = '-9999px';
                moveIcon.style.top = '-9999px';
                setHoverMode('none');
            }
        };

        const isInlineEditActive = (): boolean => {
            if (!wrapper.classList.contains('annotation-wrapper--inline-edit')) return false;
            if (wrapper.classList.contains('annotation-wrapper--editing')) return true;
            const contentEl = wrapper.querySelector('.annotation-content') as HTMLElement | null;
            if (!contentEl) return false;
            const active = document.activeElement;
            return !!(active && contentEl.contains(active));
        };

        const isOverDateControl = (clientX: number, target: EventTarget | null): boolean => {
            if (type !== 'date') return false;
            const input = wrapper.querySelector('input[type="date"]') as HTMLInputElement | null;
            if (!input) return false;
            const el = target as HTMLElement | null;
            if (!el?.closest('input[type="date"]')) return false;
            const rect = input.getBoundingClientRect();
            const calendarHitWidth = Math.max(28, Math.min(44, rect.width * 0.3));
            return clientX >= rect.right - calendarHitWidth;
        };

        const isDateControlActive = (): boolean => {
            if (type !== 'date') return false;
            if (wrapper.classList.contains('annotation-wrapper--editing')) return true;
            const input = wrapper.querySelector('input[type="date"]') as HTMLInputElement | null;
            return document.activeElement === input;
        };

        const onHoverMove = (event: Event): void => {
            const mouse = event as MouseEvent;
            if (wrapper.classList.contains('is-dragging') || wrapper.classList.contains('is-resizing')) {
                return;
            }
            if ((mouse.target as HTMLElement).closest('.close-btn')) return;
            if (isInlineEditActive() || isDateControlActive()) {
                hideIcon();
                return;
            }
            if (isOverDateControl(mouse.clientX, mouse.target)) {
                hideIcon();
                return;
            }
            if (isInResizeZone(mouse.clientX, mouse.clientY)) {
                hideIcon();
                setHoverMode('resize');
                return;
            }
            setHoverMode('move');
            showIconAt(mouse.clientX, mouse.clientY);
        };

        wrapper.addEventListener('mousemove', onHoverMove);
        wrapper.addEventListener('mouseenter', onHoverMove);
        wrapper.addEventListener('mouseleave', hideIcon);

        const content = wrapper.querySelector('.annotation-content') as HTMLElement | null;
        if (content && wrapper.classList.contains('annotation-wrapper--inline-edit')) {
            const markEditing = (): void => {
                wrapper.classList.add('annotation-wrapper--editing');
                hideIcon();
            };
            const clearEditing = (): void => {
                wrapper.classList.remove('annotation-wrapper--editing');
                hideIcon();
            };
            content.addEventListener('focusin', markEditing);
            content.addEventListener('input', markEditing);
            content.addEventListener('focusout', clearEditing);
        }

        if (type === 'date') {
            const dateInput = wrapper.querySelector('input[type="date"]') as HTMLInputElement | null;
            dateInput?.addEventListener('focusin', () => {
                wrapper.classList.add('annotation-wrapper--editing');
                hideIcon();
            });
            dateInput?.addEventListener('focusout', () => {
                wrapper.classList.remove('annotation-wrapper--editing');
                hideIcon();
            });
        }

        const beginDrag = (event: PointerEvent): void => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();

            const pageEl = wrapper.closest('.pdf-page') as HTMLElement | null;
            if (!pageEl) return;

            this.commitAnnotationDragPosition(wrapper);
            this.selectAnnotation(wrapper, type);
            wrapper.classList.add('is-dragging');
            wrapper.dataset['dragging'] = '1';
            setHoverMode('move');
            showIconAt(event.clientX, event.clientY);

            const zoom = this.getPdfZoomScale();
            const startPointerX = event.clientX;
            const startPointerY = event.clientY;
            const startLeft = parseFloat(wrapper.style.left || '0') || 0;
            const startTop = parseFloat(wrapper.style.top || '0') || 0;
            let moved = false;

            const onMove = (e: PointerEvent) => {
                const dx = (e.clientX - startPointerX) / zoom;
                const dy = (e.clientY - startPointerY) / zoom;
                if (Math.abs(dx) + Math.abs(dy) > 2) {
                    moved = true;
                }
                wrapper.style.left = `${startLeft + dx}px`;
                wrapper.style.top = `${startTop + dy}px`;
                positionIcon(e.clientX, e.clientY);
            };

            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
                wrapper.classList.remove('is-dragging');

                const ann = this.annotations.find((a) => a.element === wrapper);
                if (
                    ann?.type === 'esign' &&
                    this.selfSignMode &&
                    (ann.properties?.signImgStr ?? '').toString().trim()
                ) {
                    this.applySignatureToCanonicalFootprint(wrapper);
                }

                wrapper.dataset['dragging'] = moved ? '1' : '0';
                if (moved) {
                    requestAnimationFrame(() => {
                        wrapper.dataset['dragging'] = '0';
                    });
                } else {
                    hideIcon();
                }
            };

            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
        };

        moveIcon.addEventListener('pointerdown', (event: PointerEvent) => {
            if (!moveIcon.classList.contains('annotation-move-icon--visible')) return;
            if (isInResizeZone(event.clientX, event.clientY)) return;
            beginDrag(event);
        });

        const isTouchLikePointer = (event: PointerEvent): boolean =>
            event.pointerType === 'touch' || event.pointerType === 'pen';

        const shouldDeferTouchDragForTap = (): boolean => {
            if (type === 'date' || type === 'checkbox') return true;
            return (
                wrapper.classList.contains('annotation-wrapper--self-sign-target') &&
                !wrapper.classList.contains('annotation-wrapper--signed') &&
                !wrapper.classList.contains('annotation-wrapper--inline-edit')
            );
        };

        wrapper.addEventListener('pointerdown', (event: PointerEvent) => {
            if (!isTouchLikePointer(event)) return;
            if ((event.target as HTMLElement).closest('.close-btn, .annotation-move-icon')) return;
            if (isInlineEditActive() || isDateControlActive()) return;
            if (isOverDateControl(event.clientX, event.target)) return;
            if (isInResizeZone(event.clientX, event.clientY)) return;

            if (shouldDeferTouchDragForTap()) {
                const startX = event.clientX;
                const startY = event.clientY;
                const dragThreshold = 8;
                let dragStarted = false;

                const cleanup = (): void => {
                    window.removeEventListener('pointermove', onDeferredMove);
                    window.removeEventListener('pointerup', onDeferredUp);
                    window.removeEventListener('pointercancel', onDeferredUp);
                };

                const onDeferredMove = (moveEvent: PointerEvent): void => {
                    if (dragStarted) return;
                    const dx = moveEvent.clientX - startX;
                    const dy = moveEvent.clientY - startY;
                    if (Math.abs(dx) + Math.abs(dy) <= dragThreshold) return;
                    dragStarted = true;
                    cleanup();
                    beginDrag(moveEvent);
                };

                const onDeferredUp = (): void => {
                    cleanup();
                };

                window.addEventListener('pointermove', onDeferredMove);
                window.addEventListener('pointerup', onDeferredUp);
                window.addEventListener('pointercancel', onDeferredUp);
                return;
            }

            beginDrag(event);
        });
    }

    /** Convert screen coords to page-local px (textLayer space, zoom-safe). */
    private clientToPageLocal(clientX: number, clientY: number, pageEl: HTMLElement): { x: number; y: number } {
        const frame = this.getPageTagCoordinateFrame(pageEl);
        const zoom = this.getPdfZoomScale();
        return {
            x: (clientX - frame.refRect.left) / zoom,
            y: (clientY - frame.refRect.top) / zoom,
        };
    }

    private getFieldPlacementFromClient(
        clientX: number,
        clientY: number,
        tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp"
    ): { x: number; y: number; page: number; pageEl: HTMLElement } | null {
        if (!this.pdfContainer?.nativeElement) return null;

        const pdfPages = Array.from(this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')) as HTMLElement[];

        for (let i = 0; i < pdfPages.length; i++) {
            const pageEl = pdfPages[i];
            const frame = this.getPageTagCoordinateFrame(pageEl);
            if (frame.refWidth < 1 || frame.refHeight < 1) continue;

            const refRect = frame.refRect;
            if (
                clientY >= refRect.top &&
                clientY <= refRect.bottom &&
                clientX >= refRect.left &&
                clientX <= refRect.right
            ) {
                const dims = this.resolveToolDimensions(tool, pageEl);
                const local = this.clientToPageLocal(clientX, clientY, pageEl);
                const rawX = local.x - dims.width / 2;
                const rawY = local.y - dims.height / 2;
                const x = Math.max(0, Math.min(rawX, frame.refWidth - dims.width));
                const y = Math.max(0, Math.min(rawY, frame.refHeight - dims.height));
                return { x, y, page: i + 1, pageEl };
            }
        }

        return null;
    }

    private resolveToolDimensions(
        tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp",
        pageEl: HTMLElement,
    ): { width: number; height: number } {
        if (this.selfSignMode) {
            if (tool === 'esign') {
                return this.getCanonicalSignaturePixelSize(pageEl);
            }
            return this.getSelfSignResponsiveFieldSize(tool, pageEl);
        }
        const dims = PdfViewEditComponent.TOOL_DIMENSIONS[tool];
        return { width: dims.width, height: dims.height };
    }

    private getCanonicalSignaturePixelSize(pageEl: HTMLElement): { width: number; height: number } {
        const frame = this.getPageTagCoordinateFrame(pageEl);
        const canonical = this.getCanonicalSignatureSizePercents(frame.refWidth, frame.refHeight);
        return {
            width: Math.round((canonical.width / 100) * frame.refWidth),
            height: Math.round((canonical.height / 100) * frame.refHeight),
        };
    }

    /** Page-local bounds — same px space as placement (`style.left` / `style.top`). */
    private getAnnotationLocalBounds(
        wrapper: HTMLElement,
        pageElement?: HTMLElement | null,
    ): {
        left: number;
        top: number;
        width: number;
        height: number;
        centerX: number;
        centerY: number;
    } {
        const page = pageElement ?? (wrapper.closest('.pdf-page') as HTMLElement | null);
        const tx = parseFloat(wrapper.getAttribute('data-x') || '0') || 0;
        const ty = parseFloat(wrapper.getAttribute('data-y') || '0') || 0;

        if (page) {
            const parsedLeft = parseFloat(wrapper.style.left);
            const parsedTop = parseFloat(wrapper.style.top);
            if (Number.isFinite(parsedLeft) && Number.isFinite(parsedTop)) {
                const left = parsedLeft + tx;
                const top = parsedTop + ty;
                const width = wrapper.offsetWidth;
                const height = wrapper.offsetHeight;
                return {
                    left,
                    top,
                    width,
                    height,
                    centerX: left + width / 2,
                    centerY: top + height / 2,
                };
            }

            const frame = this.getPageTagCoordinateFrame(page);
            const rect = wrapper.getBoundingClientRect();
            const zoom = this.getPdfZoomScale();
            const left = (rect.left - frame.refRect.left) / zoom;
            const top = (rect.top - frame.refRect.top) / zoom;
            const width = rect.width / zoom;
            const height = rect.height / zoom;

            return {
                left,
                top,
                width,
                height,
                centerX: left + width / 2,
                centerY: top + height / 2,
            };
        }

        const left = wrapper.offsetLeft + tx;
        const top = wrapper.offsetTop + ty;
        const width = wrapper.offsetWidth;
        const height = wrapper.offsetHeight;
        return {
            left,
            top,
            width,
            height,
            centerX: left + width / 2,
            centerY: top + height / 2,
        };
    }

    private createFieldDragGhost(
        tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp"
    ): HTMLDivElement {
        const dims = PdfViewEditComponent.TOOL_DIMENSIONS[tool];
        const ghostFontSize = Math.max(11, Math.round(dims.height * 0.3));
        const dragEl = document.createElement('div');
        dragEl.style.cssText = [
            'position:absolute;top:-9999px;left:0;',
            `width:${dims.width}px;height:${dims.height}px;`,
            'display:flex;align-items:center;justify-content:center;',
            'background:rgba(255,255,255,0.85);',
            'border:2px dashed #FA6E16;border-radius:4px;',
            'box-shadow:0 4px 14px rgba(250,110,22,0.2);',
            `color:#9a3412;font-size:${ghostFontSize}px;font-weight:600;font-style:italic;`,
            'pointer-events:none;box-sizing:border-box;',
        ].join('');
        dragEl.textContent = dims.label;
        return dragEl;
    }

    private ensureDropPreview(
        tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp",
        pageEl?: HTMLElement,
    ): HTMLDivElement {
        if (!this._dropPreviewEl) {
            this._dropPreviewEl = document.createElement('div');
            this._dropPreviewEl.className = 'field-drop-preview';
        }

        const dims = pageEl
            ? this.resolveToolDimensions(tool, pageEl)
            : PdfViewEditComponent.TOOL_DIMENSIONS[tool];
        const label = PdfViewEditComponent.TOOL_DIMENSIONS[tool].label;
        const previewFontSize = Math.max(11, Math.round(dims.height * 0.3));
        this._dropPreviewEl.style.width = `${dims.width}px`;
        this._dropPreviewEl.style.height = `${dims.height}px`;
        this._dropPreviewEl.style.fontSize = `${previewFontSize}px`;
        this._dropPreviewEl.textContent = label;
        return this._dropPreviewEl;
    }

    private updateDropPreview(
        clientX: number,
        clientY: number,
        tool: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp"
    ): void {
        const placement = this.getFieldPlacementFromClient(clientX, clientY, tool);
        if (!placement) {
            this.removeDropPreview();
            return;
        }

        const preview = this.ensureDropPreview(tool, placement.pageEl);
        const fieldHost =
            (placement.pageEl.querySelector('.textLayer') as HTMLElement | null) ?? placement.pageEl;
        if (this._dropPreviewPage !== fieldHost) {
            this.removeDropPreview();
            this._dropPreviewPage = fieldHost;
            fieldHost.appendChild(preview);
        }

        preview.style.left = `${placement.x}px`;
        preview.style.top = `${placement.y}px`;
    }

    private removeDropPreview(): void {
        if (this._dropPreviewEl?.parentElement) {
            this._dropPreviewEl.parentElement.removeChild(this._dropPreviewEl);
        }
        this._dropPreviewPage = null;
    }

    onToolDragStart(event: DragEvent, tool: TFieldToolId): void {
        if (!event.dataTransfer) return;
        if (this.isToolDisabled(tool)) {
            event.preventDefault();
            return;
        }

        event.stopPropagation();
        this._toolDragMoved = false;
        this._draggingTool = tool;
        this.draggingToolId = tool;
        this.pdfEditState.setSelectedTool(null);

        event.dataTransfer.setData('text/plain', tool);
        event.dataTransfer.setData('application/x-field-tool', tool);
        event.dataTransfer.effectAllowed = 'copy';

        const dragEl = this.createFieldDragGhost(tool);
        document.body.appendChild(dragEl);
        const dims = PdfViewEditComponent.TOOL_DIMENSIONS[tool];
        event.dataTransfer.setDragImage(dragEl, dims.width / 2, dims.height / 2);
        setTimeout(() => dragEl.remove(), 0);
        this.cdr.markForCheck();
    }

    onToolDragEnd(): void {
        this._toolDragMoved = false;
        this._draggingTool = null;
        this.draggingToolId = null;
        this._lastFieldDragPointer = null;
        this.isPdfDragOver = false;
        this.removeDropPreview();
        this.cdr.markForCheck();
    }

    onPdfDragEnter(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        this._toolDragMoved = true;
        this.isPdfDragOver = true;
        this.cdr.markForCheck();
    }

    private isFieldTool(value: string | null | undefined): value is NonNullable<typeof this._draggingTool> {
        return !!value && !!PdfViewEditComponent.TOOL_DIMENSIONS[value];
    }

    private resolveDragTool(dataTransfer: DataTransfer | null | undefined): NonNullable<typeof this._draggingTool> | null {
        const raw = dataTransfer?.getData('application/x-field-tool') || dataTransfer?.getData('text/plain');
        return this.isFieldTool(raw) ? raw : null;
    }

    onPdfDragOver(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        this.isPdfDragOver = true;

        const tool = this._draggingTool ?? this.resolveDragTool(event.dataTransfer);

        if (tool) {
            this._lastFieldDragPointer = { clientX: event.clientX, clientY: event.clientY };
            this.updateDropPreview(event.clientX, event.clientY, tool);
        }

        this.cdr.markForCheck();
    }

    onPdfDragLeave(event: DragEvent): void {
        const container = this.pdfContainer?.nativeElement;
        if (!container) return;
        const related = event.relatedTarget as Node | null;
        if (!related || !container.contains(related)) {
            this.isPdfDragOver = false;
            this._lastFieldDragPointer = null;
            this.removeDropPreview();
            this.cdr.markForCheck();
        }
    }

    onPdfDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isPdfDragOver = false;
        this.removeDropPreview();
        this.cdr.markForCheck();

        const tool = this._draggingTool ?? this.resolveDragTool(event.dataTransfer);
        if (!tool || this.isToolDisabled(tool)) return;

        const pointer = this._lastFieldDragPointer;
        const clientX = pointer?.clientX ?? event.clientX;
        const clientY = pointer?.clientY ?? event.clientY;
        this._lastFieldDragPointer = null;

        const placement = this.getFieldPlacementFromClient(clientX, clientY, tool);
        if (!placement) return;

        this._justAddedByClick = true;
        this.addAnnotation(tool, placement.x, placement.y, placement.page);
        this.pdfEditState.setSelectedTool(null);
        this._draggingTool = null;
        this.draggingToolId = null;
        this._toolDragMoved = false;
    }

    private async renderPDF(file: File, fileKey?: string): Promise<void> {
        const seq = ++this._renderSeq;
        try {
            if (!this.pdfContainer?.nativeElement || !this.pdfPagesHost?.nativeElement) return;
            const pdfContainerEl = this.pdfContainer.nativeElement;
            const pagesRoot = this.pdfPagesHost.nativeElement;

            const buffer = await file.arrayBuffer();
            if (seq !== this._renderSeq) return;

            const typedArray = new Uint8Array(buffer);
            const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
            if (seq !== this._renderSeq) return;

            this.totalPages = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
            this.pageThumbnails = new Array(pdf.numPages).fill('');

            let pdfWidth = pdfContainerEl.clientWidth;
            if (pdfWidth <= 0) pdfWidth = 680;

            pagesRoot.innerHTML = '';
            pagesRoot.style.zoom = '';

            for (let i = 1; i <= pdf.numPages; i++) {
                if (seq !== this._renderSeq) return;

                const page = await pdf.getPage(i);
                if (seq !== this._renderSeq) return;

                const baseViewport = page.getViewport({ scale: 1 });
                const scale = pdfWidth / baseViewport.width;
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const context = canvas.getContext('2d')!;
                await page.render({ canvasContext: context, viewport, canvas }).promise;
                if (seq !== this._renderSeq) return;

                await this.capturePageThumbnail(page, i - 1, seq);
                if (seq !== this._renderSeq) return;

                const pageDiv = document.createElement('div');
                pageDiv.classList.add('pdf-page', 'page');
                pageDiv.style.position = 'relative';
                pageDiv.appendChild(canvas);

                const textLayer = document.createElement('div');
                textLayer.className = 'textLayer';
                textLayer.style.position = 'absolute';
                textLayer.style.left = '0';
                textLayer.style.top = '0';
                textLayer.style.width = '100%';
                textLayer.style.height = '100%';
                textLayer.style.overflow = 'visible';
                textLayer.style.pointerEvents = 'none';
                pageDiv.appendChild(textLayer);

                pagesRoot.appendChild(pageDiv);
            }

            if (seq === this._renderSeq) {
                this._lastRenderedFileKey = fileKey ?? this._fileKey(file);
                this._lastRenderedWidth = pdfContainerEl.clientWidth;
                this.currentPageIndex = 1;
                this.applyPdfZoom();
                this.attachPdfScrollListener();
                this.cdr.markForCheck();
            }
        } catch (err) {
            if (seq !== this._renderSeq) return;
            console.error('[PdfViewEdit] renderPDF error:', err);
        }
    }

    private async capturePageThumbnail(
        page: pdfjsLib.PDFPageProxy,
        index: number,
        seq: number,
    ): Promise<void> {
        const baseViewport = page.getViewport({ scale: 1 });
        const thumbWidth = 108;
        const scale = thumbWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        if (seq !== this._renderSeq) return;

        this.pageThumbnails[index] = canvas.toDataURL('image/jpeg', 0.72);
        this.cdr.markForCheck();
    }

    private static readonly DEFAULT_FIELD_SIZES = PdfViewEditComponent.DEFAULT_FIELD_INNER;

    private getAnnotationContentElement(wrapper: HTMLElement): HTMLElement | null {
        if (this.isSelfSignSignTagHost(wrapper)) {
            return (
                wrapper.querySelector(`.${Constants.SIGN_COMMENT_CLASS}`) as HTMLElement | null
            ) ?? (wrapper.querySelector('mat-form-field') as HTMLElement | null);
        }
        return (
            wrapper.querySelector(':scope > .annotation-content') as HTMLElement | null
        ) ?? (wrapper.querySelector(':scope > div:not(.close-btn)') as HTMLElement | null);
    }

    private getSelfSignCommentPlaceholderFontSize(innerWidth: number): number {
        const text = this.getSelfSignCommentPlaceholder();
        const measure = document.createElement('span');
        measure.style.cssText = [
            'position:absolute',
            'visibility:hidden',
            'white-space:nowrap',
            'pointer-events:none',
            'font-style:italic',
            'font-weight:600',
            'font-family:Arial, Helvetica, sans-serif',
        ].join(';');

        let fontSize = 11;
        const minFontSize = 9;
        const available = Math.max(innerWidth - 16, 72);
        while (fontSize > minFontSize) {
            measure.style.fontSize = `${fontSize}px`;
            measure.textContent = text;
            document.body.appendChild(measure);
            const fits = measure.offsetWidth <= available;
            measure.remove();
            if (fits) break;
            fontSize -= 1;
        }
        return fontSize;
    }

    private getScaledLabelFontSize(_type: string, innerWidth: number, innerHeight: number): number {
        const heightFactor = 0.42;
        const widthFactor = 0.14;
        return Math.max(9, Math.round(Math.min(innerHeight * heightFactor, innerWidth * widthFactor, 48)));
    }

    private applySelfSignCommentFieldLayout(
        wrapper: HTMLElement,
        boxWidth: number,
        boxHeight: number,
    ): void {
        const contentEl = wrapper.querySelector('.self-sign-comment-field') as HTMLElement | null;
        const border = wrapper.querySelector('.self-sign-comment-field__border') as HTMLElement | null;
        const suffixEl = wrapper.querySelector('.self-sign-comment-field__suffix') as HTMLElement | null;

        if (contentEl) {
            contentEl.style.display = 'block';
            contentEl.style.width = '100%';
            contentEl.style.height = '100%';
            contentEl.style.overflow = 'visible';
            contentEl.style.boxSizing = 'border-box';
        }
        if (border) {
            border.style.overflow = 'visible';
        }

        this.applySelfSignFieldScale(wrapper, 'comment', boxWidth, boxHeight);

        if (suffixEl) {
            const suffixText = suffixEl.textContent ?? Constants.DEFAULT_COMMENT_SUFFIX;
            const available = Math.max(boxWidth - 24, 72);
            suffixEl.style.whiteSpace = 'nowrap';
            suffixEl.style.overflow = 'hidden';
            suffixEl.style.textOverflow = 'ellipsis';

            const measure = document.createElement('span');
            measure.style.cssText = [
                'position:absolute',
                'visibility:hidden',
                'white-space:nowrap',
                'pointer-events:none',
                'font-family:Arial, Helvetica, sans-serif',
                'font-weight:normal',
            ].join(';');

            let fontSize = 8;
            const minFontSize = 6;
            while (fontSize >= minFontSize) {
                measure.style.fontSize = `${fontSize}px`;
                measure.textContent = suffixText;
                document.body.appendChild(measure);
                const fits = measure.offsetWidth <= available;
                measure.remove();
                if (fits) break;
                fontSize -= 0.5;
            }
            suffixEl.style.fontSize = `${Math.max(fontSize, minFontSize)}px`;
            suffixEl.style.lineHeight = '1.15';
        }
    }

    private applyAnnotationContentSize(
        wrapper: HTMLElement,
        contentEl: HTMLElement,
        type: string,
        boxWidth: number,
        boxHeight: number
    ): void {
        if (wrapper.classList.contains('annotation-wrapper--self-sign-comment-filled')) {
            this.applySelfSignCommentFieldLayout(wrapper, boxWidth, boxHeight);
            return;
        }

        const innerWidth = Math.max(24, boxWidth - PdfViewEditComponent.WRAPPER_CHROME);
        const innerHeight = Math.max(20, boxHeight - PdfViewEditComponent.WRAPPER_CHROME);

        contentEl.style.width = '100%';
        contentEl.style.height = '100%';
        contentEl.style.flex = '1 1 auto';
        contentEl.style.display = 'flex';
        contentEl.style.alignItems = 'center';
        contentEl.style.justifyContent = 'center';
        contentEl.style.boxSizing = 'border-box';
        contentEl.style.overflow = 'hidden';

        const selfSignLabeledField = contentEl.querySelector('.self-sign-tag-field') as HTMLElement | null;
        if (selfSignLabeledField && this.selfSignMode) {
            contentEl.style.display = 'flex';
            contentEl.style.alignItems = 'stretch';
            contentEl.style.justifyContent = 'stretch';
            contentEl.style.backgroundColor = 'transparent';
            contentEl.style.textAlign = 'left';
            contentEl.style.color = '#1f2937';
            contentEl.style.userSelect = 'none';
            contentEl.style.webkitUserSelect = 'none';

            const fieldColor = wrapper.dataset['fieldColor'];
            const labelEl = selfSignLabeledField.querySelector('.self-sign-tag-field__label') as HTMLElement | null;
            const valueEl = selfSignLabeledField.querySelector('.self-sign-tag-field__value') as HTMLElement | null;
            const dateInput = selfSignLabeledField.querySelector('.self-sign-tag-field__date-input') as HTMLInputElement | null;
            const checkboxLabel = selfSignLabeledField.querySelector('.self-sign-tag-field__checkbox-label') as HTMLElement | null;

            if (type === 'initials' && valueEl) {
                valueEl.style.userSelect = 'text';
                valueEl.style.webkitUserSelect = 'text';
                valueEl.style.whiteSpace = 'nowrap';
                const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 16);
                const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['initials'];
                const scale = Math.min(innerWidth / defaultSize.width, innerHeight / defaultSize.height, 1);
                const fontSize = Math.max(
                    12,
                    Math.min(Math.round(baseFont * Math.max(scale, 0.85)), Math.round(innerHeight * 0.72)),
                );
                valueEl.style.fontSize = `${fontSize}px`;
                valueEl.style.lineHeight = '1.1';
                valueEl.style.color = fieldColor || '#111827';
            } else if (type === 'text' && valueEl) {
                valueEl.style.userSelect = 'text';
                valueEl.style.webkitUserSelect = 'text';
                valueEl.style.whiteSpace = 'pre-wrap';
                valueEl.style.wordBreak = 'break-word';
                const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
                const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['text'];
                const scale = innerHeight / defaultSize.height;
                valueEl.style.fontSize = `${Math.max(8, Math.round(baseFont * scale))}px`;
                valueEl.style.lineHeight = '1.2';
                valueEl.style.color = fieldColor || '#111827';
            } else if (type === 'date' && dateInput) {
                const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
                const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['date'];
                const scale = innerHeight / defaultSize.height;
                const fontSize = Math.max(8, Math.round(baseFont * scale));
                dateInput.style.width = '100%';
                dateInput.style.fontSize = `${fontSize}px`;
                dateInput.style.boxSizing = 'border-box';
                dateInput.style.border = 'none';
                dateInput.style.outline = 'none';
                dateInput.style.background = 'transparent';
                dateInput.style.color = '#111827';
            } else if (type === 'checkbox') {
                const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
                const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['checkbox'];
                const scale = innerHeight / defaultSize.height;
                const fontSize = Math.max(8, Math.round(baseFont * scale));
                if (checkboxLabel) {
                    checkboxLabel.style.fontSize = `${fontSize}px`;
                    checkboxLabel.style.minWidth = '0';
                    checkboxLabel.style.flex = '1 1 auto';
                    checkboxLabel.style.overflow = 'hidden';
                    checkboxLabel.style.textOverflow = 'ellipsis';
                    checkboxLabel.style.whiteSpace = 'nowrap';
                }
                const checkboxBox = selfSignLabeledField.querySelector('.self-sign-tag-field__checkbox-box') as HTMLElement | null;
                if (checkboxBox) {
                    const box = Math.max(12, Math.round(fontSize * 1.1));
                    checkboxBox.style.width = `${box}px`;
                    checkboxBox.style.height = `${box}px`;
                }
            }

            if (labelEl) {
                labelEl.style.fontSize = `${Math.max(9, Math.round(innerHeight * 0.28))}px`;
            }
            return;
        }

        const fieldColor = wrapper.dataset['fieldColor'];
        const isInlineEditable = type === 'text' || type === 'initials';
        const isCommentLabel = type === 'comment' && !contentEl.querySelector('img');
        const isStampLabel = type === 'stamp' && !contentEl.querySelector('img');
        contentEl.style.textAlign = isInlineEditable ? 'left' : 'center';
        if ((isInlineEditable || isCommentLabel || isStampLabel) && fieldColor) {
            contentEl.style.color = fieldColor;
        } else if (!isInlineEditable && !isCommentLabel && !isStampLabel) {
            contentEl.style.color = '#1f2937';
        } else {
            contentEl.style.color = '#000000';
        }
        contentEl.style.backgroundColor = '#ffffff';
        contentEl.style.userSelect = isInlineEditable ? 'text' : 'none';
        contentEl.style.webkitUserSelect = isInlineEditable ? 'text' : 'none';
        contentEl.style.minWidth = '0';
        contentEl.style.minHeight = '0';

        if (isCommentLabel && this.selfSignMode) {
            contentEl.style.lineHeight = '1.15';
            contentEl.style.fontSize = `${this.getSelfSignCommentPlaceholderFontSize(innerWidth)}px`;
            contentEl.style.fontWeight = '600';
            contentEl.style.fontStyle = 'italic';
            contentEl.style.whiteSpace = 'nowrap';
            contentEl.style.overflow = 'hidden';
            contentEl.style.textOverflow = 'ellipsis';
            contentEl.style.textAlign = 'center';
            contentEl.style.padding = '0 6px';
        } else if (type === 'esign' || isCommentLabel || isStampLabel) {
            contentEl.style.lineHeight = '1.2';
            contentEl.style.fontSize = `${this.getScaledLabelFontSize(type, innerWidth, innerHeight)}px`;
            contentEl.style.fontWeight = '600';
        } else if (type === 'initials') {
            contentEl.style.userSelect = 'text';
            contentEl.style.webkitUserSelect = 'text';
            contentEl.style.whiteSpace = 'nowrap';
            contentEl.style.fontWeight = 'normal';
            const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 16);
            const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['initials'];
            const scale = Math.min(innerWidth / defaultSize.width, innerHeight / defaultSize.height, 1);
            const fontSize = Math.max(
                12,
                Math.min(Math.round(baseFont * Math.max(scale, 0.85)), Math.round(innerHeight * 0.72)),
            );
            contentEl.style.fontSize = `${fontSize}px`;
            contentEl.style.lineHeight = '1.1';
            contentEl.style.alignItems = 'center';
            contentEl.style.justifyContent = 'flex-start';
        } else if (type === 'text') {
            contentEl.style.userSelect = 'text';
            contentEl.style.webkitUserSelect = 'text';
            contentEl.style.whiteSpace = 'pre-wrap';
            contentEl.style.wordBreak = 'break-word';
            contentEl.style.overflowWrap = 'anywhere';
            const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
            const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['text'];
            const scale = innerHeight / defaultSize.height;
            contentEl.style.fontSize = `${Math.max(8, Math.round(baseFont * scale))}px`;
            contentEl.style.lineHeight = '1.2';
            contentEl.style.alignItems = 'flex-start';
            contentEl.style.justifyContent = 'flex-start';
        } else if (type === 'date') {
            const input = contentEl.querySelector('input') as HTMLInputElement | null;
            const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
            const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['date'];
            const scale = innerHeight / defaultSize.height;
            const fontSize = Math.max(8, Math.round(baseFont * scale));
            if (input) {
                input.style.width = '100%';
                input.style.height = '100%';
                input.style.maxWidth = '100%';
                input.style.fontSize = `${fontSize}px`;
                input.style.boxSizing = 'border-box';
                input.style.border = 'none';
                input.style.outline = 'none';
                input.style.background = 'transparent';
            }
        } else if (type === 'checkbox') {
            const baseFont = Number(wrapper.dataset['baseFontSize'] || this.selectedFontSize || 14);
            const defaultSize = PdfViewEditComponent.DEFAULT_FIELD_SIZES['checkbox'];
            const scale = innerHeight / defaultSize.height;
            const fontSize = Math.max(8, Math.round(baseFont * scale));
            contentEl.style.fontSize = `${fontSize}px`;
            contentEl.style.gap = `${Math.max(4, Math.round(fontSize * 0.35))}px`;
            contentEl.style.alignItems = 'center';
            contentEl.style.justifyContent = 'flex-start';
            contentEl.style.minWidth = '0';
            const input = contentEl.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            if (input) {
                const box = Math.max(12, Math.round(fontSize * 1.1));
                input.style.width = `${box}px`;
                input.style.height = `${box}px`;
                input.style.flexShrink = '0';
            }
            const label = contentEl.querySelector('span') as HTMLElement | null;
            if (label) {
                label.style.minWidth = '0';
                label.style.flex = '1 1 auto';
                label.style.overflow = 'hidden';
                label.style.textOverflow = 'ellipsis';
                label.style.whiteSpace = 'nowrap';
            }
        }
    }

    private syncSelfSignSignTagHostWidth(host: HTMLElement): void {
        const type = host.dataset['annotationType'];
        if (type !== 'text' && type !== 'initials' && type !== 'checkbox') return;
        this._signService.prepareSelfSignTagHostForUpload(host, type);
        const newWidth = Math.max(host.offsetWidth, 1);
        const newHeight = Math.max(host.offsetHeight, 1);
        host.style.width = `${newWidth}px`;
        host.style.maxWidth = `${newWidth}px`;
        const resizable = host.querySelector('.resizable') as HTMLElement | null;
        if (resizable && type === 'checkbox') {
            resizable.style.width = `${newWidth}px`;
        }
        this.applySelfSignFieldScale(host, type, newWidth, newHeight);
    }

    private attachSelfSignSignTagInputWidthSync(host: HTMLElement): void {
        const type = host.dataset['annotationType'];
        if (type === 'text' || type === 'initials') {
            const dataType = type === 'initials' ? 'INITIALS' : 'TEXT';
            const bind = () => {
                const input = host.querySelector(
                    `mat-form-field[data-type="${dataType}"] input`,
                ) as HTMLInputElement | null;
                if (!input || input.dataset['selfSignWidthSync'] === '1') return;
                input.dataset['selfSignWidthSync'] = '1';
                input.addEventListener('input', () => this.syncSelfSignSignTagHostWidth(host));
                this.syncSelfSignSignTagHostWidth(host);
            };

            bind();
            setTimeout(bind, 0);
            setTimeout(bind, 120);
            return;
        }

        if (type !== 'checkbox') return;

        const bindCheckbox = () => {
            const input = host.querySelector('.sign-tag-checkbox-label') as HTMLInputElement | null;
            if (!input || input.dataset['selfSignWidthSync'] === '1') return;
            input.dataset['selfSignWidthSync'] = '1';
            input.addEventListener('input', () => this.syncSelfSignSignTagHostWidth(host));
            this.syncSelfSignSignTagHostWidth(host);
        };

        bindCheckbox();
        setTimeout(bindCheckbox, 0);
        setTimeout(bindCheckbox, 120);
    }

    private syncInitialsWrapperWidth(wrapper: HTMLElement, contentEl: HTMLElement): void {
        const valueEl = contentEl.querySelector('.self-sign-tag-field__value') as HTMLElement | null;
        const measureEl = valueEl ?? contentEl;
        const text = (measureEl.innerText ?? measureEl.textContent ?? '').replace(/\n/g, '');
        const minWidth = PdfViewEditComponent.getDefaultWrapperSize('initials').width;
        if (!text.trim()) {
            if (wrapper.offsetWidth > minWidth) {
                wrapper.style.width = `${minWidth}px`;
                this.syncAnnotationWrapperSize(wrapper);
            }
            return;
        }

        const fontSize = Number(
            measureEl.style.fontSize?.replace('px', '') ||
            contentEl.style.fontSize?.replace('px', '') ||
            wrapper.dataset['baseFontSize'] ||
            16,
        );
        const measure = document.createElement('span');
        measure.style.cssText = [
            'position:absolute',
            'visibility:hidden',
            'white-space:nowrap',
            'pointer-events:none',
            `font-size:${fontSize}px`,
            'font-family:Arial, Helvetica, sans-serif',
            'font-weight:normal',
        ].join(';');
        measure.textContent = text;
        document.body.appendChild(measure);
        const textWidth = measure.offsetWidth;
        measure.remove();

        const neededWidth = Math.max(minWidth, textWidth + PdfViewEditComponent.WRAPPER_CHROME + 20);
        if (Math.abs(wrapper.offsetWidth - neededWidth) > 2) {
            wrapper.style.width = `${neededWidth}px`;
            this.syncAnnotationWrapperSize(wrapper);
        }
    }

    private syncAnnotationWrapperSize(wrapper: HTMLElement, width?: number, height?: number): void {
        const type = wrapper.dataset['annotationType'];
        const contentEl = this.getAnnotationContentElement(wrapper);
        if (!type || !contentEl) return;

        const boxWidth = width ?? wrapper.offsetWidth;
        const boxHeight = height ?? wrapper.offsetHeight;
        if (boxWidth <= 0 || boxHeight <= 0) return;

        if (this.isSelfSignSignTagHost(wrapper)) {
            this.applySelfSignFieldScale(wrapper, type, boxWidth, boxHeight);
        } else if (wrapper.classList.contains('annotation-wrapper--self-sign-comment-filled')) {
            this.applySelfSignFieldScale(wrapper, 'comment', boxWidth, boxHeight);
        }

        this.applyAnnotationContentSize(wrapper, contentEl, type, boxWidth, boxHeight);
    }

    private setAnnotationSelected(wrapper: HTMLElement, selected: boolean): void {
        if (this.isSelfSignSignTagHost(wrapper)) {
            const resizable = wrapper.querySelector('.resizable') as HTMLElement | null;
            document.querySelectorAll('.resizable.selected').forEach((el) => {
                if (el !== resizable) {
                    el.classList.remove('selected');
                }
            });
            resizable?.classList.toggle('selected', selected);
            return;
        }

        wrapper.classList.toggle('selected', selected);
        wrapper.style.border = '2px solid #3f51b5';
        wrapper.style.borderRadius = '4px';
        wrapper.style.boxShadow = selected ? '0 0 8px rgba(63, 81, 181, 0.55)' : 'none';
    }

    private clearAllAnnotationSelection(): void {
        this.annotations.forEach((annotation) => {
            this.setAnnotationSelected(annotation.element as HTMLElement, false);
        });
    }

    private addAnnotation(type: "text" | "checkbox" | "date" | "esign" | "comment" | "initials" | "stamp", x: number, y: number, page?: number, dimensions?: { width: number; height: number }): void {
        const selfSignLabeledField =
            this.selfSignMode &&
            (type === 'text' || type === 'initials' || type === 'date' || type === 'checkbox');

        if (selfSignLabeledField) {
            this.addSelfSignSignTagField(type, x, y, page, dimensions);
            return;
        }

        const wrapper = document.createElement('div');
        const pageElementForSize = this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')[(page || 1) - 1] as
            | HTMLElement
            | undefined;
        const responsiveSize = this.selfSignMode
            ? this.getSelfSignResponsiveFieldSize(type, pageElementForSize ?? null)
            : null;
        const canonicalSelfSignSize = this.selfSignMode
            ? this.getSelfSignCanonicalWrapperSize(type)
            : null;
        const defaultWrapperSize = PdfViewEditComponent.getDefaultWrapperSize(type);
        let wrapperWidth = dimensions?.width ?? responsiveSize?.width ?? canonicalSelfSignSize?.width ?? defaultWrapperSize.width;
        let wrapperHeight = dimensions?.height ?? responsiveSize?.height ?? canonicalSelfSignSize?.height ?? defaultWrapperSize.height;
        let posX = x;
        let posY = y;

        if (this.selfSignMode && pageElementForSize) {
            const clamped = this.clampSelfSignFieldRect(posX, posY, wrapperWidth, wrapperHeight, pageElementForSize);
            posX = clamped.x;
            posY = clamped.y;
            wrapperWidth = clamped.width;
            wrapperHeight = clamped.height;
        }

        wrapper.classList.add('annotation-wrapper');
        wrapper.dataset['annotationType'] = type;
        wrapper.dataset['baseFontSize'] = String(
            type === 'initials' ? (this.selectedFontSize || 16) : (this.selectedFontSize || 14),
        );
        wrapper.style.position = 'absolute';
        wrapper.style.left = `${posX}px`;
        wrapper.style.top = `${posY}px`;
        wrapper.style.width = `${wrapperWidth}px`;
        wrapper.style.height = `${wrapperHeight}px`;
        if (this.selfSignMode && type === 'comment') {
            wrapper.style.maxWidth = `${wrapperWidth}px`;
            this.applySelfSignFieldScale(wrapper, type, wrapperWidth, wrapperHeight);
        }
        wrapper.style.boxSizing = 'border-box';
        wrapper.style.display = 'flex';
        wrapper.style.flexDirection = 'column';
        wrapper.style.alignItems = 'stretch';
        wrapper.style.justifyContent = 'stretch';

        const createSignTransparentField =
            !this.selfSignMode &&
            (type === 'text' || type === 'initials' || type === 'date' || type === 'checkbox');
        wrapper.style.backgroundColor = createSignTransparentField ? 'transparent' : '#ffffff';
        wrapper.style.padding = '4px';
        const inlineField = this.selfSignMode && (type === 'date' || type === 'checkbox');
        wrapper.style.border = inlineField
            ? '1px solid #000000'
            : '2px solid #3f51b5';
        wrapper.style.setProperty('--annotation-border-width', inlineField ? '1px' : '2px');
        wrapper.style.borderRadius = '4px';
        wrapper.style.boxShadow = 'none';

        if (!this.selfSignMode) {
            if (type === 'text' || type === 'initials') {
                wrapper.classList.add('annotation-wrapper--inline-edit');
            }
            if (type === 'date') {
                wrapper.classList.add('annotation-wrapper--date-field');
            }
        }

        wrapper.draggable = false;
        wrapper.style.opacity = '1';

        wrapper.onclick = (event) => {
            event.stopPropagation();
            if (wrapper.dataset['dragging'] === '1') {
                wrapper.dataset['dragging'] = '0';
                return;
            }
            if (this.selfSignMode && type === 'esign') {
                void this.handleSelfSignFieldClick(wrapper);
                return;
            }
            if (this.selfSignMode && type === 'stamp') {
                this.selectAnnotation(wrapper, type);
                this.pickStampImageFromComputer(wrapper);
                return;
            }
            if (this.selfSignMode && type === 'comment') {
                this.selectAnnotation(wrapper, type);
                void this.openSelfSignCommentDialog(wrapper);
                return;
            }
            this.selectAnnotation(wrapper, type);
        };

        if (this.selfSignMode && (type === 'esign' || type === 'stamp' || type === 'comment')) {
            wrapper.classList.add('annotation-wrapper--self-sign-target');
            if (type === 'comment') {
                wrapper.classList.add('annotation-wrapper--self-sign-comment-placeholder');
            }
            wrapper.style.cursor = 'pointer';
        }

        let annotationContent: HTMLDivElement | null = null;
        if (type === 'esign') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.textContent = this.getSelfSignFieldLabel('esign');
            annotationContent.style.textAlign = 'center';
            annotationContent.style.fontStyle = 'italic';
        } else if (type === 'initials') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.contentEditable = 'true';
            annotationContent.textContent = this.getSelfSignFieldLabel('initials');
            annotationContent.style.outline = 'none';
            annotationContent.style.color = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
            annotationContent.style.fontSize = `${this.selectedFontSize || 16}px`;
            annotationContent.addEventListener('input', () => {
                if (annotationContent) {
                    this.syncInitialsWrapperWidth(wrapper, annotationContent);
                }
            });
        } else if (type === 'comment') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.textContent = this.selfSignMode
                ? this.getSelfSignCommentPlaceholder()
                : this.getSelfSignFieldLabel('comment');
            annotationContent.style.textAlign = 'center';
            annotationContent.style.fontStyle = 'italic';
            annotationContent.style.color = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
        } else if (type === 'stamp') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.textContent = this.getSelfSignFieldLabel('stamp');
            annotationContent.style.textAlign = 'center';
            annotationContent.style.fontStyle = 'italic';
            annotationContent.style.color = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
        } else if (type === 'text') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.contentEditable = 'true';
            annotationContent.textContent = this.getSelfSignFieldLabel('text');
            annotationContent.style.outline = 'none';
            annotationContent.style.color = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
            annotationContent.style.fontSize = `${this.selectedFontSize || 14}px`;
        } else if (type === 'date') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.innerHTML = `
                <input type="date" style="font-size: ${this.selectedFontSize}px;" placeholder="" />
            `;
            if (this.selfSignMode) {
                const dateInput = annotationContent.querySelector('input[type="date"]') as HTMLInputElement | null;
                if (dateInput) {
                    dateInput.value = this.getTodayDateIso();
                }
            }
        } else if (type === 'checkbox') {
            annotationContent = document.createElement('div');
            annotationContent.className = 'annotation-content';
            annotationContent.style.display = 'flex';
            const checkboxLabel = this.selfSignMode
                ? this.getSelfSignFieldLabel('checkbox')
                : (this.placeholder || 'Editable Label');
            annotationContent.innerHTML = `
                <input type="checkbox"/>
                <span contenteditable="true" style="outline: none;">${checkboxLabel}</span>
            `;
        }

        if (annotationContent) {
            annotationContent.style.pointerEvents = 'all';
            wrapper.appendChild(annotationContent);
        }

        const moveIcon = document.createElement('span');
        moveIcon.classList.add('annotation-move-icon', 'material-icons');
        moveIcon.setAttribute('aria-hidden', 'true');
        moveIcon.textContent = 'open_with';
        wrapper.appendChild(moveIcon);
        this.attachAnnotationFieldHoverMove(wrapper, moveIcon, type);

        const closeButton = document.createElement('div');
        closeButton.classList.add('close-btn');
        closeButton.innerHTML = '&times;';
        closeButton.onclick = (event) => {
            event.stopPropagation();
            wrapper.remove();
            if (this.selectedAnnotation && this.selectedAnnotation.element === wrapper) {
                this.selectedAnnotation = null;
                this.pdfEditState.setSelectedAnnotation(null);
                this.cdr.detectChanges();
            }
        };
        wrapper.appendChild(closeButton);

        interact(wrapper)
            .resizable({
                edges: { left: true, right: true, top: true, bottom: true },
                margin: 10,
                ignoreFrom: '.annotation-move-icon, .close-btn',
                listeners: {
                    start: () => {
                        wrapper.classList.add('is-resizing');
                        moveIcon.classList.remove('annotation-move-icon--visible');
                    },
                    move: (event) => {
                        const target = event.target as HTMLElement;
                        const zoom = this.getPdfZoomScale();
                        const { width, height } = event.rect;
                        target.style.width = `${width}px`;
                        target.style.height = `${height}px`;

                        const x =
                            (parseFloat(target.getAttribute('data-x')!) || 0) + event.deltaRect.left / zoom;
                        const y =
                            (parseFloat(target.getAttribute('data-y')!) || 0) + event.deltaRect.top / zoom;

                        target.style.transform = `translate3d(${x}px, ${y}px, 0)`;
                        target.setAttribute('data-x', x.toString());
                        target.setAttribute('data-y', y.toString());
                        this.syncAnnotationWrapperSize(target, width, height);
                    },
                    end: (event) => {
                        wrapper.classList.remove('is-resizing');
                        const target = event.target as HTMLElement;
                        this.syncAnnotationWrapperSize(target, event.rect.width, event.rect.height);
                        this.commitAnnotationDragPosition(target);
                        const ann = this.annotations.find((a) => a.element === target);
                        if (ann?.type === 'esign' && this.selfSignMode &&
                            (ann.properties?.signImgStr ?? '').toString().trim()) {
                            this.applySignatureToCanonicalFootprint(target);
                        }
                    },
                },
            });

        const pageElement = this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')[(page || 1) - 1];
        const fieldHost =
            (pageElement?.querySelector('.textLayer') as HTMLElement | null) ?? pageElement;
        if (fieldHost) {
            fieldHost.appendChild(wrapper);
        }

        if (annotationContent) {
            this.applyAnnotationContentSize(wrapper, annotationContent, type, wrapperWidth, wrapperHeight);
        }
        // this.pdfContainer.nativeElement.appendChild(wrapper);
        const pageNumber = this.getPageNumberForAnnotation(wrapper);

        const defaultProperties = {
            placeholder: this.selfSignMode ? '' : 'placeholder',
            fontSize: type === 'initials' ? 16 : 14,
            user: this.selfSignMode ? this.resolveSelfSignAssigneeEmail() : this.resolveDefaultAssigneeEmail(),
            page: pageNumber || 1,
            ...(type === 'text' || type === 'comment' || type === 'initials' || (this.selfSignMode && type === 'esign')
                ? { signatureColor: DEFAULT_SELF_SIGN_SIGNATURE_COLOR }
                : {}),
        };

        if (type === 'text' || type === 'initials' || type === 'comment') {
            wrapper.dataset['fieldColor'] = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
        }

        const annotation = {
            element: wrapper,
            type,
            properties: { ...defaultProperties },
        };

        this.annotations.push(annotation);

        this.selectAnnotation(wrapper, type);
    }

    /** Self-sign stamp — open native file picker directly (no dialog). */
    private pickStampImageFromComputer(wrapper: HTMLElement): void {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png, image/jpeg, image/jpg';
        input.style.display = 'none';
        document.body.appendChild(input);

        input.onchange = () => {
            const file = input.files?.[0];
            input.remove();
            if (!file) return;

            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                this._toastr.showError('Please choose a PNG or JPEG image');
                return;
            }
            if (file.size > Constants.MAX_SIGN_UPLOAD_FILE_SIZE_IN_KB) {
                this._toastr.showError('Image file is too large');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const imageBase64 = (reader.result as string) ?? '';
                if (!imageBase64.trim()) return;
                this.applySignatureToAnnotation(wrapper, 'stamp', { imageBase64 });
            };
            reader.onerror = () => this._toastr.showError('Could not read the selected image');
            reader.readAsDataURL(file);
        };

        input.click();
    }

    private async openSelfSignCommentDialog(wrapper: HTMLElement): Promise<void> {
        const signer = this.tableService.tableInputData[0];
        const dialogRef = this._dialog.open(UploadSignComponent, {
            width: '640px',
            maxWidth: '95vw',
            panelClass: ['signature-dialog', 'rounded-dialog'],
            backdropClass: 'blur-backdrop',
            data: {
                isComment: true,
                isStamp: false,
                isResponsiveContainer: false,
                signerName: signer?.name ?? '',
                selfSignMode: true,
            },
        });
        const data = await firstValueFrom(dialogRef.afterClosed());
        if (!data?.imageBase64) return;
        this.applySignatureToAnnotation(wrapper, 'comment', data);
    }

    /** Label shown in the field editor sidebar and sent as API `docName`. */
    get displayDocumentName(): string {
        return this.resolveDocNameForSubmit();
    }

    private resolveDocNameForSubmit(): string {
        const custom = (this.docName ?? '').trim();
        if (custom) return custom;
        return this.pdfFile?.name ?? '';
    }

    async submit(requesterDetails: {
        name: string;
        phone: string;
        email: string;
        emailSubject?: string;
        message?: string;
    }): Promise<void> {
        // Guard rails: avoid "click does nothing" due to runtime errors / missing async data.
        if (!this.pdfFile) {
            this._toastr.showError('Please upload a PDF first');
            return;
        }
        if (!requesterDetails?.name || !requesterDetails?.email) {
            this._toastr.showError('Please fill requester details');
            return;
        }
        if (!this.isDraftRequest && !(this.appToken && (this.appToken as any).Token)) {
            this._toastr.showError('Please select App Token');
            return;
        }

        this.ensureAnnotationAssignees();
        const unassigned = this.annotations.filter(
            (a) => !(a.properties?.user ?? '').toString().trim(),
        );
        if (unassigned.length > 0) {
            this._toastr.showError('Please assign each field to a signer in the Property panel');
            return;
        }

        // Ensure base64 is ready (FileReader is async; submit can be clicked before it's set).
        try {
            await this.ensurePdfBase64();
        } catch {
            this._toastr.showError('PDF is still loading. Please try again.');
            return;
        }

        const requesterPhone = this.tableService.optionalPhoneForSignRequest(
            requesterDetails.phone
        );

        const formattedData = {
            docContent: this.pdfBase64,
            docName: this.resolveDocNameForSubmit(),
            ...(this.isDraftRequest ? {} : { appToken: this.appToken.Token }),
            requesterEmail: requesterDetails.email,
            requesterName: requesterDetails.name,
            ...(requesterPhone != null ? { requesterPhone } : {}),
            requesterMessage: requesterDetails.message ?? '',
            newRequestEmailSubject: requesterDetails.emailSubject ?? '',
            ...(this.isDraftRequest ? { draftSignRequestId: this._activatedRoute.snapshot.params["requestID"] } : {}),
            description: "Test03March",
            signers: this.buildSignersPayload(),
            callbackUrl: "abc",
        };

        const hasNonCommentAnnotations = this.annotations.some(
            annotation => {
                const user = this.tableService.tableInputData.find(data => data.email === annotation.properties.user);
                if (!user) return false;
                return annotation.type.toLowerCase() !== EnumSignTagTypes.COMMENT.toLowerCase() &&
                    (user.role || '').toLowerCase() === EnumSignerRoleType.REVIEWER.toLowerCase();
            }
        );

        if (hasNonCommentAnnotations) {
            this._toastr.showError('Only comments are allowed for reviewers');
            return;
        }

        this._appService.updatePageLoaderVisibility(true, 'Submitting data...');

        // Create endpoint using HttpService
        const apiUrl = this.httpService.createEndpoint(
            Constants.API_URI_SIGN_REQUEST_STR,
            Constants.API_ENDPOINT
        );

        // Use HttpService to send a POST request
        this.httpService.post(apiUrl, formattedData).subscribe({
            next: (response) => {
                this._appService.updatePageLoaderVisibility(false);
                this.onSubmitComplete.emit();
            },
            error: (error) => {
                this.httpService.showHttpError(error);
                this._appService.updatePageLoaderVisibility(false);
            }
        });
    }

    async submitSelfSign(requesterDetails: {
        name: string;
        phone: string;
        email: string;
        emailSubject?: string;
        message?: string;
    }): Promise<void> {
        if (!this.pdfFile) {
            this._toastr.showError('Please upload a PDF first');
            return;
        }
        if (!requesterDetails?.name || !requesterDetails?.email) {
            this._toastr.showError('Please fill requester details');
            return;
        }
        if (!(this.appToken && (this.appToken as IAppToken).Token)) {
            this._toastr.showError('Please select App Token');
            return;
        }

        this.ensureAnnotationAssignees();
        if (!this.ensureSelfSignAssigneesReady()) {
            return;
        }

        await new Promise<void>((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        );

        this.ensureSelfSignCopiedEsignFieldsSigned();

        for (const annotation of this.annotations) {
            if (
                annotation.type === 'esign' ||
                annotation.type === 'stamp' ||
                annotation.type === 'comment'
            ) {
                this.syncSelfSignSignImgStrFromDom(annotation);
            }
        }

        const unsignedSignFields = this.annotations.filter((a) =>
            this.isSelfSignRequiredFieldIncomplete(a),
        );
        if (unsignedSignFields.length > 0) {
            this._toastr.showError('Please complete each signature, stamp, and comment field before submitting');
            return;
        }

        const signableFields = this.annotations.filter(
            (a) => this.isSelfSignFieldType(a.type) || this.isSelfSignInlineFieldType(a.type),
        );
        const hasSignedEsign = this.annotations.some((a) => {
            if (a.type !== 'esign') return false;
            const wrapper = a.element as HTMLElement;
            if (this.isSelfSignFieldSigned(wrapper)) return true;
            if (wrapper.querySelector('img[data-value-available="true"]')) return true;
            return !!(a.properties?.['signImgStr'] ?? '').toString().trim();
        });
        if (!signableFields.length || !hasSignedEsign) {
            this._toastr.showError('Please add and sign at least one signature field before submitting');
            return;
        }

        try {
            await this.ensurePdfBase64();
        } catch {
            this._toastr.showError('PDF is still loading. Please try again.');
            return;
        }

        let signatures: ISignRequestAndUploadSignature[];
        try {
            signatures = await this.buildSelfSignUploadSignaturesAsync();
        } catch {
            this._toastr.showError('Could not read signature image. Please re-sign and try again.');
            return;
        }
        if (!signatures.length) {
            this._toastr.showError('Signature image data is missing. Please click each field and sign again.');
            return;
        }

        const signedEsignFields = this.annotations.filter(
            (a) =>
                a.type === 'esign' &&
                !!(a.properties?.['signImgStr'] ?? '').toString().trim(),
        ).length;
        const uploadedSignatureCount = signatures.filter(
            (s) =>
                (s.type ?? '').toUpperCase() === 'SIGNATURE' &&
                this.isRealSignatureImage(s.signImgStr),
        ).length;
        if (signedEsignFields > 0 && uploadedSignatureCount < signedEsignFields) {
            this._toastr.showError(
                'Could not read every signature. Please re-sign each signature field and try again.',
            );
            return;
        }

        const requesterPhone = this.tableService.optionalPhoneForSignRequest(
            requesterDetails.phone
        );
        const signers = this.buildSignersPayload();
        const createSignRequestDetails = {
            docContent: this.pdfBase64!,
            docName: this.resolveDocNameForSubmit(),
            appToken: (this.appToken as IAppToken).Token,
            requesterEmail: requesterDetails.email,
            requesterName: requesterDetails.name,
            ...(requesterPhone != null ? { requesterPhone } : {}),
            requesterMessage: requesterDetails.message ?? '',
            newRequestEmailSubject: requesterDetails.emailSubject ?? '',
            description: 'Test03March',
            signers,
            callbackUrl: 'abc',
            returnFirstSignerURLFlag: false,
            isDraftRequest: false,
        };

        const payload: ISignRequestAndUploadRequest = {
            createSignRequestDetails,
            uploadSignDetails: {
                signRequestId: '',
                signerId: '',
                ERSD_Accepted: true,
                signatures,
            },
        };

        this._appService.updatePageLoaderVisibility(true, 'Submitting self sign...');
        const apiUrl = this.httpService.createEndpoint(
            Constants.API_URI_SIGN_REQUEST_AND_UPLOAD,
            Constants.API_ENDPOINT,
        );

        this.httpService.post(apiUrl, payload).subscribe({
            next: (response: unknown) => {
                this._appService.updatePageLoaderVisibility(false);
                const signRequestId = this.readSignRequestIdFromResponse(response);
                if (!signRequestId) {
                    this._toastr.showError(Messages.MSG_ERROR_UNEXPECTED);
                    return;
                }
                this.selfSignComplete.emit({
                    signRequestId,
                    docName: this.resolveDocNameForSubmit(),
                });
            },
            error: (error) => {
                this.httpService.showHttpError(error);
                this._appService.updatePageLoaderVisibility(false);
            },
        });
    }

    private readSignRequestIdFromResponse(response: unknown): string {
        const payload = response as Record<string, unknown>;
        const data = (payload?.['Data'] ?? payload?.['data'] ?? payload) as Record<
            string,
            unknown
        >;
        return String(data?.['SignRequestId'] ?? data?.['signRequestId'] ?? '').trim();
    }

    private buildSignersPayload(): ICreateSignRequestSigner[] {
        return this.tableService.tableInputData.map((user, index) => {
            const userAnnotations = this.annotations
                .filter((annotation) => {
                    const assigned = (annotation.properties?.user ?? '')
                        .toString()
                        .trim()
                        .toLowerCase();
                    return assigned === (user.email ?? '').toString().trim().toLowerCase();
                })
                .map((annotation) => this.buildSignerTagFromAnnotation(annotation))
                .filter((annotation) => annotation !== null);

            const tagsString = userAnnotations.map((tag) => JSON.stringify(tag)).join(',');
            const signerPhone = this.tableService.optionalPhoneForSignRequest(user.phone);
            const emailCC = this.tableService.emailCcForSignRequest(user);
            const allowedActions = this.tableService.allowedActionsForSignRequest(user);

            return {
                signerName: user.name,
                signerEmail: user.email,
                order: index + 1,
                SignerRole: user.role,
                ...(signerPhone != null ? { phone: signerPhone } : {}),
                emailCC,
                ...(allowedActions != null ? { allowedActions } : {}),
                id: user.id ?? null,
                Tags: tagsString,
            } as ICreateSignRequestSigner;
        });
    }

    /** Sign/Upload API footprint — visible 19.5% width, centered on where the user placed the field. */
    private getSelfSignUploadMetrics(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): {
        pageIndex: number;
        xPercent: number;
        yPercent: number;
        imgWidthPercent: number;
        imgHeightPercent: number;
        apiType: string;
    } | null {
        const pageElement = annotation.element.closest('.pdf-page') as HTMLElement | null;
        if (!pageElement) return null;

        const frame = this.getPageTagCoordinateFrame(pageElement);
        const bounds = this.getAnnotationLocalBounds(annotation.element as HTMLElement, pageElement);
        const apiType =
            (annotation.properties?.['uploadType'] as string | undefined) ??
            this.mapAnnotationTypeToApi(annotation.type);
        const pageIndex = Math.max(0, ((annotation.properties?.['page'] as number) || 1) - 1);

        let imgWidthPercent: number;
        let imgHeightPercent: number;

        if (apiType === EnumSignTagTypes.SIGNATURE) {
            const canonical = this.getCanonicalSignatureSizePercents(
                frame.refWidth,
                frame.refHeight,
            );
            imgWidthPercent = canonical.width;
            imgHeightPercent = canonical.height;
        } else if (apiType === EnumSignTagTypes.COMMENT) {
            imgWidthPercent = this.formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE);
            imgHeightPercent = this.formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE);
        } else {
            imgWidthPercent = this.formatTagPercent((bounds.width / frame.refWidth) * 100);
            imgHeightPercent = this.formatTagPercent((bounds.height / frame.refHeight) * 100);
        }

        return {
            pageIndex,
            xPercent: this.formatTagPercent((bounds.left / frame.refWidth) * 100),
            yPercent: this.formatTagPercent((bounds.top / frame.refHeight) * 100),
            imgWidthPercent,
            imgHeightPercent,
            apiType,
        };
    }

    private getCanonicalSignatureSizePercents(
        pageWidth: number,
        pageHeight: number,
    ): { width: number; height: number } {
        const safeW = Math.max(pageWidth, 1);
        const safeH = Math.max(pageHeight, 1);
        const widthPercent = Constants.CANONICAL_SIGN_WIDTH_PERCENT;
        const widthPx = (widthPercent / 100) * safeW;
        const heightPx = widthPx * Constants.CANONICAL_SIGN_ASPECT;
        return {
            width: this.formatTagPercent(widthPercent),
            height: this.formatTagPercent((heightPx / safeH) * 100),
        };
    }

    /** Resize signed esign field to canonical px footprint (center stays on user placement). */
    private applySignatureToCanonicalFootprint(wrapper: HTMLElement): void {
        const pageElement = wrapper.closest('.pdf-page') as HTMLElement | null;
        if (!pageElement) return;

        const frame = this.getPageTagCoordinateFrame(pageElement);
        const bounds = this.getAnnotationLocalBounds(wrapper, pageElement);
        const canonical = this.getCanonicalSignatureSizePercents(frame.refWidth, frame.refHeight);
        const widthPx = (canonical.width / 100) * frame.refWidth;
        const heightPx = (canonical.height / 100) * frame.refHeight;
        const sizeMatches =
            Math.abs(bounds.width - widthPx) < 3 && Math.abs(bounds.height - heightPx) < 3;

        if (sizeMatches) {
            this.syncAnnotationWrapperSize(wrapper, widthPx, heightPx);
            return;
        }

        const leftPx = bounds.centerX - widthPx / 2;
        const topPx = bounds.centerY - heightPx / 2;

        wrapper.style.width = `${widthPx}px`;
        wrapper.style.height = `${heightPx}px`;
        wrapper.style.minWidth = `${widthPx}px`;
        wrapper.style.minHeight = `${heightPx}px`;
        wrapper.style.left = `${Math.max(0, Math.min(leftPx, frame.refWidth - widthPx))}px`;
        wrapper.style.top = `${Math.max(0, Math.min(topPx, frame.refHeight - heightPx))}px`;
        wrapper.style.transform = 'translate(0px, 0px)';
        wrapper.setAttribute('data-x', '0');
        wrapper.setAttribute('data-y', '0');
        this.syncAnnotationWrapperSize(wrapper, widthPx, heightPx);
    }

    private ensureSelfSignCanonicalFootprints(): void {
        if (!this.selfSignMode) return;
        for (const annotation of this.annotations) {
            if (annotation.type !== 'esign') continue;
            if (!(annotation.properties?.signImgStr ?? '').toString().trim()) continue;
            this.applySignatureToCanonicalFootprint(annotation.element as HTMLElement);
        }
    }

    private async resolveSignImgStrForSubmit(annotation: {
        element: Element;
        properties?: Record<string, unknown>;
    }): Promise<string> {
        const wrapper = annotation.element as HTMLElement;
        const fromDom = await this.resolveSignImgStrFromAnnotation(wrapper, '');
        if (fromDom?.trim()) return fromDom;

        const fromService = await this._signService.extractSignImageBase64(annotation.element);
        if (fromService?.trim()) return fromService;

        const stored = (annotation.properties?.['signImgStr'] ?? '').toString().trim();
        if (stored) {
            return this.ensureSignImgBase64(stored);
        }
        return '';
    }

    /** Real signature PNG — not the 1×1 placeholder used for inline fields. */
    private isRealSignatureImage(signImgStr: string | undefined | null): boolean {
        return (signImgStr ?? '').trim().length > 120;
    }

    /** Stamp/comment images are always persisted; esign keeps the placeholder guard. */
    private shouldPersistSignImgStr(stripped: string, type: string): boolean {
        const len = stripped.trim().length;
        if (!len) return false;
        if (type === 'stamp' || type === 'comment') return len > 20;
        return this.isRealSignatureImage(stripped);
    }

    private syncSelfSignSignImgStrFromDom(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): void {
        const wrapper = annotation.element as HTMLElement;
        const img = wrapper.querySelector(
            'img[data-value-available="true"]',
        ) as HTMLImageElement | null;
        if (!img?.src?.trim()) return;

        const stripped = img.src.startsWith('data:')
            ? this.stripBase64Prefix(img.src)
            : img.src.trim();
        if (!this.shouldPersistSignImgStr(stripped, annotation.type)) return;

        annotation.properties = annotation.properties ?? {};
        annotation.properties['signImgStr'] = stripped;
    }

    private isSelfSignRequiredFieldIncomplete(annotation: {
        type: string;
        element: Element;
        properties?: Record<string, unknown>;
    }): boolean {
        if (!this.isSelfSignFieldType(annotation.type)) return false;

        const wrapper = annotation.element as HTMLElement;
        if (annotation.type === 'esign') {
            if (this.isSelfSignFieldSigned(wrapper)) {
                return false;
            }

            const hasDomImage = !!wrapper.querySelector('img[data-value-available="true"]');
            const hasSignImgStr = !!(annotation.properties?.['signImgStr'] ?? '').toString().trim();
            if (hasDomImage || hasSignImgStr) {
                wrapper.dataset['selfSignSigned'] = 'true';
                wrapper.classList.add('annotation-wrapper--signed');
                return false;
            }

            return true;
        }

        // Stamp/comment placeholders left empty after canceling picker/modal are optional.
        if (wrapper.dataset['selfSignSigned'] !== 'true') {
            return false;
        }

        const hasDomImage = !!wrapper.querySelector('img[data-value-available="true"]');
        const hasSignImgStr = !!(annotation.properties?.['signImgStr'] ?? '').toString().trim();
        return !hasDomImage && !hasSignImgStr;
    }

    /** After copy-to-all, unsigned esign clones get the same signature as the signed source field. */
    private ensureSelfSignCopiedEsignFieldsSigned(): void {
        if (!this.selfSignMode) return;

        const signedTemplate = this.annotations.find((annotation) => {
            if (annotation.type !== 'esign') return false;
            const wrapper = annotation.element as HTMLElement;
            return (
                this.isSelfSignFieldSigned(wrapper) ||
                !!wrapper.querySelector('img[data-value-available="true"]') ||
                !!(annotation.properties?.['signImgStr'] ?? '').toString().trim()
            );
        });
        if (!signedTemplate) return;

        const imageBase64 = this.resolveSelfSignAnnotationImageDataUrl({
            element: signedTemplate.element as HTMLElement,
            type: signedTemplate.type,
            properties: signedTemplate.properties,
        });
        if (!imageBase64) return;

        for (const annotation of this.annotations) {
            if (annotation.type !== 'esign') continue;

            const wrapper = annotation.element as HTMLElement;
            if (this.isSelfSignFieldSigned(wrapper)) continue;

            const hasDomImage = !!wrapper.querySelector('img[data-value-available="true"]');
            const hasSignImgStr = !!(annotation.properties?.['signImgStr'] ?? '').toString().trim();
            if (hasDomImage || hasSignImgStr) {
                wrapper.dataset['selfSignSigned'] = 'true';
                wrapper.classList.add('annotation-wrapper--signed');
                continue;
            }

            this.applySignatureToAnnotation(wrapper, 'esign', {
                imageBase64,
                signatureId: (signedTemplate.properties?.['signatureId'] as string) ?? '',
                signComment: (signedTemplate.properties?.['signComment'] as string) ?? '',
                comment: (signedTemplate.properties?.['commentText'] as string) ?? '',
                sourceImageBase64:
                    (signedTemplate.properties?.['sourceImageBase64'] as string) ?? undefined,
                signatureColor: (signedTemplate.properties?.['signatureColor'] as string) ?? undefined,
                force: true,
            });
        }
    }

    private resolveSelfSignAnnotationImageDataUrl(annotation: {
        element: HTMLElement;
        type: string;
        properties?: Record<string, unknown>;
    }): string | null {
        const fromDom = this.getAnnotationImageDataUrl(annotation);
        if (fromDom) return fromDom;

        const signImgStr = (annotation.properties?.['signImgStr'] ?? '').toString().trim();
        if (!signImgStr) return null;
        return signImgStr.startsWith('data:') ? signImgStr : `data:image/png;base64,${signImgStr}`;
    }

    /** Same tag + upload bounds as Sign/Upload `getRequestDetails`. */
    private buildSignerTagFromAnnotation(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): {
        Type: string;
        XLocation: number;
        YLocation: number;
        Width: number;
        Height: number;
        Text: string;
        Page: number;
    } | null {
        const host = annotation.element as HTMLElement;
        const pageElement = host.closest('.pdf-page') as HTMLElement | null;
        if (!pageElement) return null;

        const uploadType = this.mapUploadDataType(annotation.type);
        const bounds = this._signService.getHostUploadPercentBounds(host, pageElement, uploadType);

        return {
            Type: this.mapAnnotationTypeToApi(annotation.type),
            XLocation: bounds.xPercent,
            YLocation: bounds.yPercent,
            Width: bounds.imgWidthPercent,
            Height: bounds.imgHeightPercent,
            Text: this.getSignerTagText(annotation),
            Page: (annotation.properties?.['page'] as number) || 1,
        };
    }

    private getSignerTagText(annotation: {
        element: Element;
        type: string;
    }): string {
        if (this.isSelfSignInlineFieldType(annotation.type)) {
            return this._signService.getSelfSignInlineTagText(
                annotation.element as HTMLElement,
                annotation.type,
            );
        }
        return this.normalizeTagTextForApi(this.getAnnotationTagText(annotation));
    }

    /** Shared % bounds for create-request Tags — always matches on-page placement. */
    private getAnnotationPercentBounds(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): {
        pageIndex: number;
        xPercent: number;
        yPercent: number;
        widthPercent: number;
        heightPercent: number;
        apiType: string;
    } | null {
        return this.measureAnnotationPercentBounds(annotation);
    }

    /** Measure field footprint on the page canvas — matches where the user placed/signed. */
    private measureAnnotationPercentBounds(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): {
        pageIndex: number;
        xPercent: number;
        yPercent: number;
        widthPercent: number;
        heightPercent: number;
        apiType: string;
    } | null {
        const pageElement = annotation.element.closest('.pdf-page') as HTMLElement | null;
        if (!pageElement) return null;

        const frame = this.getPageTagCoordinateFrame(pageElement);
        const bounds = this.getAnnotationLocalBounds(annotation.element as HTMLElement, pageElement);
        const apiType =
            (annotation.properties?.['uploadType'] as string | undefined) ??
            this.mapAnnotationTypeToApi(annotation.type);
        const pageIndex = Math.max(0, ((annotation.properties?.['page'] as number) || 1) - 1);

        const widthPercent = this.formatTagPercent((bounds.width / frame.refWidth) * 100);
        const heightPercent = this.formatTagPercent((bounds.height / frame.refHeight) * 100);

        return {
            pageIndex,
            xPercent: this.formatTagPercent((bounds.left / frame.refWidth) * 100),
            yPercent: this.formatTagPercent((bounds.top / frame.refHeight) * 100),
            widthPercent,
            heightPercent,
            apiType,
        };
    }

    /** Same canvas frame used when placing fields (left/top px → % on submit). */
    private getPageTagCoordinateFrame(pageElement: HTMLElement): {
        refRect: DOMRect;
        refWidth: number;
        refHeight: number;
    } {
        const textLayer = pageElement.querySelector('.textLayer') as HTMLElement | null;
        const canvas = pageElement.querySelector('canvas') as HTMLCanvasElement | null;
        const refEl = textLayer ?? canvas ?? pageElement;
        const refRect = refEl.getBoundingClientRect();
        const zoom = this.getPdfZoomScale();
        return {
            refRect,
            refWidth: Math.max(refEl.clientWidth || refRect.width / zoom, 1),
            refHeight: Math.max(refEl.clientHeight || refRect.height / zoom, 1),
        };
    }

    private formatTagPercent(value: number): number {
        return parseFloat(value.toFixed(14));
    }

    private isProbablyRawBase64(value: string): boolean {
        const v = value.trim();
        if (!v || /^https?:\/\//i.test(v) || v.startsWith('blob:') || v.startsWith('/')) {
            return false;
        }
        return /^[A-Za-z0-9+/=\s]+$/.test(v);
    }

    private loadImageSourceToBase64(src: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.max(img.naturalWidth || img.width || 1, 1);
                    canvas.height = Math.max(img.naturalHeight || img.height || 1, 1);
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas unavailable'));
                        return;
                    }
                    ctx.drawImage(img, 0, 0);
                    resolve(this.stripBase64Prefix(canvas.toDataURL('image/png')));
                } catch (err) {
                    reject(err);
                }
            };
            img.onerror = () => reject(new Error('Failed to load signature image'));
            img.src = src;
        });
    }

    private async ensureSignImgBase64(source: string): Promise<string> {
        const trimmed = (source ?? '').trim();
        if (!trimmed) return '';

        if (trimmed.startsWith('data:')) {
            return this.stripBase64Prefix(trimmed);
        }
        if (this.isProbablyRawBase64(trimmed)) {
            return trimmed.replace(/\s/g, '');
        }
        if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('blob:')) {
            return this.loadImageSourceToBase64(trimmed);
        }
        if (trimmed.startsWith('/')) {
            return this.loadImageSourceToBase64(`${window.location.origin}${trimmed}`);
        }
        return trimmed.replace(/\s/g, '');
    }

    private async resolveSignImgStrFromAnnotation(
        wrapper: HTMLElement,
        fallback: string,
    ): Promise<string> {
        const img =
            (wrapper.querySelector('img[data-value-available="true"]') as HTMLImageElement | null) ??
            (wrapper.querySelector('img') as HTMLImageElement | null);

        if (img?.src) {
            if (img.complete && img.naturalWidth > 0) {
                try {
                    const fromDom = await this.ensureSignImgBase64(img.src);
                    if (fromDom) return fromDom;
                } catch {
                    /* fall through to stored value */
                }
            } else {
                await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error('Signature image failed to load'));
                });
                try {
                    const fromDom = await this.ensureSignImgBase64(img.src);
                    if (fromDom) return fromDom;
                } catch {
                    /* fall through */
                }
            }
        }

        return this.ensureSignImgBase64(fallback);
    }

    private async buildSelfSignUploadSignaturesAsync(): Promise<ISignRequestAndUploadSignature[]> {
        const signatures: ISignRequestAndUploadSignature[] = [];

        for (const annotation of this.annotations) {
            const host = annotation.element as HTMLElement;
            const pageElement = host.closest('.pdf-page') as HTMLElement | null;
            if (!pageElement) continue;

            if (this.isSelfSignSignTagHost(host)) {
                this._signService.prepareSelfSignTagHostForUpload(host, annotation.type);
            }

            const pageIndex = Math.max(0, ((annotation.properties?.['page'] as number) || 1) - 1);
            const entry = await this._signService.collectSelfSignUploadEntry(
                host,
                pageElement,
                pageIndex,
                annotation.type,
            );
            if (!entry) continue;

            const uploadType = (entry.type ?? '').toUpperCase();
            if (uploadType === 'SIGNATURE' && !this.isRealSignatureImage(entry.signImgStr)) continue;

            annotation.properties['uploadEntry'] = entry;

            signatures.push({
                pageIndex: entry.pageIndex,
                xPercent: entry.xPercent,
                yPercent: entry.yPercent,
                signImgStr: entry.signImgStr.trim(),
                imgWidthPercent: entry.imgWidthPercent,
                imgHeightPercent: entry.imgHeightPercent,
                signComment: entry.signComment,
                signatureId: entry.signatureId ?? '',
                type: entry.type,
                commentText: entry.commentText,
                isComment: entry.isComment,
            });
        }

        return signatures;
    }

    private isSelfSignInlineFieldType(type: string): boolean {
        if (this.selfSignMode && type === 'comment') return false;
        return type === 'text' || type === 'date' || type === 'checkbox' || type === 'initials' || type === 'comment';
    }

    private getAnnotationTagText(annotation: {
        type: string;
        element: Element;
    }): string | boolean {
        const wrapper = annotation.element as HTMLElement;
        if (this.isSelfSignSignTagHost(wrapper)) {
            if (annotation.type === 'checkbox') {
                const input = wrapper.querySelector(
                    'mat-checkbox[data-type="CHECKBOX"] input[type="checkbox"]',
                ) as HTMLInputElement | null;
                return input?.checked ?? false;
            }
            if (annotation.type === 'date') {
                const input = wrapper.querySelector(
                    'mat-form-field[data-type="DATE"] input',
                ) as HTMLInputElement | null;
                return (input?.getAttribute('data-value') ?? input?.value ?? '').trim();
            }
            const dataType = annotation.type === 'initials' ? 'INITIALS' : 'TEXT';
            const input = wrapper.querySelector(
                `mat-form-field[data-type="${dataType}"] input`,
            ) as HTMLInputElement | null;
            return (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
        }

        const content = annotation.element.querySelector('.annotation-content');
        if (annotation.type === 'checkbox') {
            return (content?.querySelector('input') as HTMLInputElement | null)?.checked ?? false;
        }
        if (annotation.type === 'date') {
            const iso =
                (content?.querySelector('input[type="date"]') as HTMLInputElement | null)?.value ?? '';
            const trimmed = iso.trim();
            if (!trimmed) return '';
            const parts = trimmed.split('-');
            if (parts.length !== 3) return trimmed;
            const [yyyy, mm, dd] = parts;
            return `${dd}/${mm}/${yyyy}`;
        }
        if (annotation.type === 'esign') {
            return '';
        }
        if (annotation.type === 'text' || annotation.type === 'comment') {
            const el = content as HTMLElement | null;
            return (el?.innerText ?? content?.textContent ?? '').trim();
        }
        if (annotation.type === 'initials') {
            const el = content as HTMLElement | null;
            return (el?.innerText ?? content?.textContent ?? '').trim();
        }
        return '';
    }

    private normalizeTagTextForApi(value: string | boolean): string {
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        const text = String(value ?? '').trim();
        return text === 'Editable Text' || text === 'Initial' || text === 'Comment' || text === 'Check to confirm'
            ? ''
            : text;
    }

    private isSelfSignFieldType(type: string): boolean {
        return type === 'esign' || type === 'stamp' || (this.selfSignMode && type === 'comment');
    }

    private stripBase64Prefix(value: string): string {
        const base64Index = value.indexOf('base64,');
        return base64Index !== -1 ? value.substring(base64Index + 7) : value;
    }

    /** Ensure preview + submit always use a loadable data URL (not a bare URL or raw base64). */
    private async normalizeSignatureForSelfSign(source: string): Promise<string> {
        const trimmed = (source ?? '').trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('data:')) return trimmed;
        const base64 = await this.ensureSignImgBase64(trimmed);
        return base64 ? `data:image/png;base64,${base64}` : trimmed;
    }

    private setSelfSignActiveSignature(data: {
        imageBase64: string;
        sourceImageBase64?: string;
        signatureColor?: string;
        signatureId?: string;
        signComment?: string;
        comment?: string;
    }): void {
        const color = data.signatureColor ?? DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
        void (async () => {
            const rawSource = (data.sourceImageBase64 ?? data.imageBase64 ?? '').trim();
            if (!rawSource) return;

            const normalizedSource = await this.normalizeSignatureForSelfSign(rawSource);
            const sourceImageBase64 = normalizedSource || rawSource;
            let imageBase64 = sourceImageBase64;
            try {
                imageBase64 = await recolorSignatureImage(sourceImageBase64, color);
            } catch {
                imageBase64 = data.imageBase64 || sourceImageBase64;
            }

            this._selfSignActiveSignature = {
                imageBase64,
                sourceImageBase64: data.sourceImageBase64?.trim() || sourceImageBase64,
                signatureColor: color,
                signatureId: data.signatureId ?? '',
                signComment: data.signComment ?? '',
                comment: data.comment ?? '',
            };
            this.updateCursor();
            this.cdr.detectChanges();
        })();
    }

    private async updateSelfSignSignatureColor(color: string): Promise<void> {
        if (!this._selfSignActiveSignature) return;

        const source =
            this._selfSignActiveSignature.sourceImageBase64?.trim() ||
            this._selfSignActiveSignature.imageBase64?.trim();
        if (!source) return;

        let colored = this._selfSignActiveSignature.imageBase64;
        try {
            colored = await recolorSignatureImage(source, color);
        } catch {
            return;
        }

        this._selfSignActiveSignature = {
            ...this._selfSignActiveSignature,
            sourceImageBase64: source,
            signatureColor: color,
            imageBase64: colored,
        };

        if (this.selfSignMode) {
            const annotation = this.selectedAnnotation;
            if (!annotation || annotation.type !== 'esign') return;
            if (!(annotation.properties?.signImgStr ?? '').toString().trim()) return;
            annotation.properties.signatureColor = color;
            this.applySignatureToAnnotation(annotation.element as HTMLElement, 'esign', {
                ...this._selfSignActiveSignature,
                imageBase64: colored,
                recolor: true,
                force: true,
            });
            this.cdr.detectChanges();
            return;
        }

        for (const annotation of this.annotations) {
            if (annotation.type !== 'esign') continue;
            if (!(annotation.properties?.signImgStr ?? '').toString().trim()) continue;
            annotation.properties.signatureColor = color;
            this.applySignatureToAnnotation(annotation.element as HTMLElement, 'esign', {
                ...this._selfSignActiveSignature,
                recolor: true,
            });
        }

        this.cdr.detectChanges();
    }

    private async updateSelfSignInlineFieldColor(color: string): Promise<void> {
        const annotation = this.selectedAnnotation;
        if (!annotation) return;

        const wrapper = annotation.element as HTMLElement;
        annotation.properties.signatureColor = color;
        wrapper.dataset['fieldColor'] = color;

        if (annotation.type === 'text') {
            const content = this.getAnnotationContentElement(wrapper);
            const input = wrapper.querySelector('mat-form-field[data-type="TEXT"] .mat-mdc-input-element') as HTMLElement | null;
            if (input) {
                input.style.color = color;
            } else if (content) {
                content.style.color = color;
            }
            this.cdr.detectChanges();
            return;
        }

        if (annotation.type === 'initials') {
            const content = this.getAnnotationContentElement(wrapper);
            const input = wrapper.querySelector('mat-form-field[data-type="INITIALS"] .mat-mdc-input-element') as HTMLElement | null;
            if (input) {
                input.style.color = color;
            } else if (content) {
                content.style.color = color;
            }
            this.cdr.detectChanges();
            return;
        }

        if (annotation.type !== 'comment') return;

        const commentContent = this.getAnnotationContentElement(wrapper);
        const commentBody = commentContent?.querySelector('.self-sign-comment-field__body') as HTMLElement | null;
        if (commentBody) {
            commentBody.style.color = color;
        }
        const commentImg = commentContent?.querySelector('img[data-value-available="true"]') as HTMLImageElement | null;
        if (!commentImg) {
            if (commentContent) {
                commentContent.style.color = color;
            }
            this.cdr.detectChanges();
            return;
        }

        let source = (annotation.properties.sourceImageBase64 as string | undefined)?.trim();
        if (!source) {
            source = commentImg.src ?? '';
        }
        if (!source) return;

        let colored = source;
        try {
            colored = await recolorSignatureImage(source, color);
        } catch {
            return;
        }

        const content = this.getAnnotationContentElement(wrapper);
        const img = content?.querySelector('img[data-value-available="true"]') as HTMLImageElement | null;
        if (img) {
            img.src = colored;
        }
        if (commentBody) {
            commentBody.style.color = color;
        }

        void this.ensureSignImgBase64(colored).then((signImgStr) => {
            if (this.isRealSignatureImage(signImgStr)) {
                annotation.properties.signImgStr = signImgStr;
            }
        });

        this.cdr.detectChanges();
    }

    async openSelfSignPickerDialog(): Promise<void> {
        const data = await this.promptSelfSignSignatureDialog();
        if (data?.imageBase64) {
            this.setSelfSignActiveSignature({
                imageBase64: data.imageBase64,
                sourceImageBase64: data.sourceImageBase64,
                signatureColor: data.signatureColor,
                signatureId: data.signatureId,
                signComment: data.signComment,
                comment: data.comment,
            });
            this._toastr.showSuccess('Signature ready — add a sign field, then click it to sign.');
        }
    }

    private isSelfSignFieldSigned(wrapper: HTMLElement): boolean {
        return wrapper.dataset['selfSignSigned'] === 'true';
    }

    private async handleSelfSignFieldClick(wrapper: HTMLElement): Promise<void> {
        this.selectAnnotation(wrapper, 'esign');
        const active = this._selfSignActiveSignature;
        if (active?.imageBase64?.trim()) {
            this.applySignatureToAnnotation(wrapper, 'esign', {
                imageBase64: active.imageBase64,
                sourceImageBase64: active.sourceImageBase64,
                signatureColor: active.signatureColor,
                signatureId: active.signatureId,
                signComment: active.signComment,
                comment: active.comment,
                force: this.isSelfSignFieldSigned(wrapper),
            });
            this.cdr.detectChanges();
            return;
        }
        await this.openSelfSignFieldDialog(wrapper, 'esign');
    }

    private async promptSelfSignSignatureDialog(): Promise<{
        imageBase64?: string;
        sourceImageBase64?: string;
        signatureColor?: string;
        signComment?: string;
        signatureId?: string;
        comment?: string;
    } | undefined> {
        const signer = this.tableService.tableInputData[0];
        const dialogRef = this._dialog.open(UploadSignComponent, {
            width: '640px',
            maxWidth: '95vw',
            panelClass: ['signature-dialog', 'rounded-dialog'],
            backdropClass: 'blur-backdrop',
            data: {
                isComment: false,
                isResponsiveContainer: false,
                signerName: signer?.name ?? '',
                selfSignMode: true,
            },
        });
        return firstValueFrom(dialogRef.afterClosed());
    }

    private async openSelfSignFieldDialog(
        wrapper: HTMLElement,
        type: 'esign',
    ): Promise<void> {
        const data = await this.promptSelfSignSignatureDialog();
        if (!data?.imageBase64) return;

        const wasSigned = this.isSelfSignFieldSigned(wrapper);
        this.setSelfSignActiveSignature({
            imageBase64: data.imageBase64,
            sourceImageBase64: data.sourceImageBase64,
            signatureColor: data.signatureColor,
            signatureId: data.signatureId,
            signComment: data.signComment,
            comment: data.comment,
        });
        this.applySignatureToAnnotation(wrapper, type, { ...data, force: wasSigned });
    }

    private applySignatureToAnnotation(
        wrapper: HTMLElement,
        type: string,
        data: {
            imageBase64?: string;
            signComment?: string;
            signatureId?: string;
            comment?: string;
            sourceImageBase64?: string;
            signatureColor?: string;
            recolor?: boolean;
            force?: boolean;
        },
    ): void {
        const annotation = this.annotations.find((a) => a.element === wrapper);
        if (!annotation || !data.imageBase64) return;

        if (
            !data.force &&
            this.selfSignMode &&
            type === 'esign' &&
            this.isSelfSignFieldSigned(wrapper) &&
            !data.recolor
        ) {
            return;
        }

        if (this.selfSignMode && type === 'esign') {
            wrapper.dataset['selfSignSigned'] = 'true';
            wrapper.classList.add('annotation-wrapper--signed');
            wrapper.style.cursor = 'pointer';
            wrapper.style.border = '2px solid #24b124';
            wrapper.style.backgroundColor = '#ffffff';
        }

        if (this.selfSignMode && type === 'stamp') {
            wrapper.dataset['selfSignSigned'] = 'true';
            wrapper.classList.add('annotation-wrapper--signed');
            wrapper.style.cursor = 'pointer';
            wrapper.style.border = '2px solid #24b124';
            wrapper.style.backgroundColor = '#ffffff';
        }

        if (this.selfSignMode && type === 'comment' && data.imageBase64) {
            const fillColor =
                data.signatureColor ??
                wrapper.dataset['fieldColor'] ??
                DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
            this.renderSelfSignCommentBox(
                wrapper,
                data.comment ?? '',
                data.imageBase64,
                fillColor,
            );

            const rawImage = data.imageBase64 ?? '';
            if (rawImage.startsWith('data:')) {
                const stripped = this.stripBase64Prefix(rawImage);
                if (this.shouldPersistSignImgStr(stripped, type)) {
                    annotation.properties.signImgStr = stripped;
                }
            }
            void this.ensureSignImgBase64(rawImage).then((signImgStr) => {
                if (this.shouldPersistSignImgStr(signImgStr, type)) {
                    annotation.properties.signImgStr = signImgStr;
                }
            });
            annotation.properties.signatureId = data.signatureId ?? '';
            annotation.properties.signComment = data.signComment ?? '';
            annotation.properties.commentText = data.comment ?? '';
            annotation.properties.uploadType = this.mapAnnotationTypeToApi(type);
            annotation.properties.signatureColor = fillColor;
            wrapper.dataset['fieldColor'] = fillColor;
            if (data.sourceImageBase64) {
                annotation.properties.sourceImageBase64 = data.sourceImageBase64;
            }
            return;
        }

        const content = this.getAnnotationContentElement(wrapper);
        if (content) {
            content.className = 'annotation-content';
            content.innerHTML = '';
            content.style.padding = '0';
            content.style.margin = '0';
            content.style.overflow = 'hidden';
            content.style.fontStyle = 'normal';
            content.style.lineHeight = 'normal';
            content.style.minWidth = '0';
            content.style.minHeight = '0';
            content.style.width = '100%';
            content.style.height = '100%';
            content.style.display = 'flex';
            content.style.alignItems = 'center';
            content.style.justifyContent = 'center';

            const img = document.createElement('img');
            img.src = data.imageBase64;
            img.style.maxWidth = '100%';
            img.style.maxHeight = '100%';
            img.style.width = 'auto';
            img.style.height = 'auto';
            img.style.objectFit = 'contain';
            img.style.objectPosition = 'center';
            img.style.display = 'block';
            img.style.imageRendering = 'auto';
            img.setAttribute('data-type', this.mapUploadDataType(type));
            img.setAttribute('data-value-available', 'true');
            if (data.comment) {
                img.setAttribute('data-value', data.comment);
                wrapper.dataset['commentText'] = data.comment;
            }
            if (data.signatureId) {
                img.setAttribute('signatureid', data.signatureId);
            }
            content.appendChild(img);
        }

        const rawImage = data.imageBase64 ?? '';
        if (rawImage.startsWith('data:')) {
            const stripped = this.stripBase64Prefix(rawImage);
            if (this.shouldPersistSignImgStr(stripped, type)) {
                annotation.properties.signImgStr = stripped;
            }
        }
        void this.ensureSignImgBase64(rawImage).then((signImgStr) => {
            if (this.shouldPersistSignImgStr(signImgStr, type)) {
                annotation.properties.signImgStr = signImgStr;
            }
        });
        annotation.properties.signatureId = data.signatureId ?? '';
        annotation.properties.signComment = data.signComment ?? '';
        annotation.properties.commentText = data.comment ?? '';
        annotation.properties.uploadType = this.mapAnnotationTypeToApi(type);
        if (this.selfSignMode && (type === 'esign' || type === 'comment')) {
            const fieldColor =
                data.signatureColor ??
                (type === 'esign' ? this._selfSignActiveSignature?.signatureColor : undefined) ??
                DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
            annotation.properties.signatureColor = fieldColor;
            wrapper.dataset['fieldColor'] = fieldColor;
        }
        if (this.selfSignMode && type === 'comment' && data.sourceImageBase64) {
            annotation.properties.sourceImageBase64 = data.sourceImageBase64;
        }

        if (this.selfSignMode && type === 'esign') {
            requestAnimationFrame(() => {
                const pageElement = wrapper.closest('.pdf-page') as HTMLElement | null;
                if (!pageElement) return;
                const bounds = this.getAnnotationLocalBounds(wrapper, pageElement);
                const canonical = this.getCanonicalSignaturePixelSize(pageElement);
                const sizeMatches =
                    Math.abs(bounds.width - canonical.width) < 2 &&
                    Math.abs(bounds.height - canonical.height) < 2;
                if (sizeMatches) {
                    this.syncAnnotationWrapperSize(wrapper);
                } else {
                    this.applySignatureToCanonicalFootprint(wrapper);
                }
            });
        }

        this.selectAnnotation(wrapper, type);
        this.cdr.detectChanges();
    }

    private ensurePdfBase64(): Promise<string> {
        if (this.pdfBase64) return Promise.resolve(this.pdfBase64);
        if (!this.pdfFile) return Promise.reject(new Error('No PDF file'));
        if (this._base64InFlight) return this._base64InFlight;

        this._base64InFlight = new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64WithPrefix = event.target?.result as string;
                const base64 = (base64WithPrefix || '').split(',')[1] || '';
                if (!base64) {
                    reject(new Error('Failed to parse base64'));
                    return;
                }
                this.pdfBase64 = base64;
                resolve(base64);
            };
            reader.onerror = () => reject(new Error('Failed to read PDF'));
            reader.readAsDataURL(this.pdfFile!);
        }).finally(() => {
            this._base64InFlight = null;
        });

        return this._base64InFlight;
    }

    private convertPdfToBase64(): void {
        // Fire-and-forget; submit() will await ensurePdfBase64() if needed.
        void this.ensurePdfBase64();
    }

    selectAnnotation(wrapper: HTMLElement, type: string): void {
        this.clearAllAnnotationSelection();

        const annotation = this.annotations.find((a) => a.element === wrapper);
        if (annotation) {
            this.selectedAnnotation = annotation;
            this.setAnnotationSelected(wrapper, true);
            this.syncAnnotationWrapperSize(wrapper);
            this.pdfEditState.setSelectedAnnotation(annotation);
        }
    }

    private syncSelfSignFieldStateToProperties(annotation: {
        element: Element;
        type: string;
        properties?: Record<string, unknown>;
    }): void {
        if (!this.selfSignMode) return;

        annotation.properties = annotation.properties ?? {};
        const wrapper = annotation.element as HTMLElement;

        if (
            this.isSelfSignSignTagHost(wrapper) &&
            (annotation.type === 'text' ||
                annotation.type === 'initials' ||
                annotation.type === 'date' ||
                annotation.type === 'checkbox')
        ) {
            annotation.properties['fieldTextValue'] = this.readSelfSignTagFieldValue(
                wrapper,
                annotation.type,
            );
            if (annotation.type === 'checkbox') {
                annotation.properties['placeholder'] = this.readSelfSignCheckboxLabel(wrapper);
            }
        }

        if (annotation.type === 'esign' || annotation.type === 'stamp' || annotation.type === 'comment') {
            this.syncSelfSignSignImgStrFromDom(annotation);
        }
    }

    private readSelfSignCheckboxLabel(wrapper: HTMLElement): string {
        const component = this.getSelfSignTagComponent(wrapper);
        if (component) {
            return (component.checkboxLabelText ?? '').trim();
        }

        const labelInput = wrapper.querySelector(
            '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
        ) as HTMLInputElement | null;
        if (labelInput) {
            return (labelInput.value ?? '').trim();
        }

        const content = this.getAnnotationContentElement(wrapper);
        const labelEl = content?.querySelector('.self-sign-tag-field__checkbox-label')
            ?? content?.querySelector('span[contenteditable="true"]')
            ?? content?.querySelector('span');
        return (labelEl?.textContent ?? '').trim();
    }

    private readSelfSignTagFieldValue(wrapper: HTMLElement, type: string): string {
        const component = this.getSelfSignTagComponent(wrapper);
        if (component) {
            if (type === 'checkbox') {
                return component.tagValue ? 'true' : '';
            }
            return String(component.tagValue ?? '').trim();
        }

        if (this.isSelfSignSignTagHost(wrapper)) {
            const dataType = type === 'initials' ? 'INITIALS' : type === 'date' ? 'DATE' : 'TEXT';
            const input = wrapper.querySelector(
                `mat-form-field[data-type="${dataType}"] input`,
            ) as HTMLInputElement | null;
            if (type === 'date') {
                return (input?.getAttribute('data-value') ?? input?.value ?? '').trim();
            }
            return (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
        }

        const content = this.getAnnotationContentElement(wrapper);
        return (content?.textContent ?? '').trim();
    }

    private getSelfSignTagComponent(wrapper: HTMLElement): SignTagElementComponent | null {
        const ref = (
            wrapper as HTMLElement & { __selfSignTagComponentRef?: { instance: SignTagElementComponent } }
        ).__selfSignTagComponentRef;
        return ref?.instance ?? null;
    }

    private writeSelfSignTagFieldValue(
        wrapper: HTMLElement,
        type: string,
        value: string,
        sourceWrapper?: HTMLElement,
    ): void {
        if (!this.isSelfSignSignTagHost(wrapper)) return;

        const component = this.getSelfSignTagComponent(wrapper);
        const sourceComponent = sourceWrapper ? this.getSelfSignTagComponent(sourceWrapper) : null;

        if (component) {
            if (type === 'date') {
                if (sourceComponent?.dateObject) {
                    component.onDateChange(new Date(sourceComponent.dateObject));
                } else if (value) {
                    const [dd, mm, yyyy] = value.split('/');
                    if (dd && mm && yyyy) {
                        component.onDateChange(new Date(`${yyyy}-${mm}-${dd}T12:00:00`));
                    }
                } else {
                    component.onDateChange(null);
                }
            } else if (type === 'checkbox') {
                component.tagValue = sourceComponent?.tagValue ?? (value === 'true' ? 'true' : '');
                component.checkboxLabelText =
                    sourceComponent?.checkboxLabelText
                    ?? (sourceWrapper ? this.readSelfSignCheckboxLabel(sourceWrapper) : '')
                    ?? component.checkboxLabelText;
            } else {
                component.tagValue = value;
            }
            this.cdr.detectChanges();
        }

        const dataType = type === 'initials' ? 'INITIALS' : type === 'date' ? 'DATE' : 'TEXT';
        const input = wrapper.querySelector(
            type === 'checkbox'
                ? 'mat-checkbox[data-type="CHECKBOX"] input[type="checkbox"]'
                : `mat-form-field[data-type="${dataType}"] input`,
        ) as HTMLInputElement | null;
        if (input && type !== 'checkbox') {
            input.value = value;
            input.setAttribute('data-value', value);
            input.setAttribute('data-value-available', value.trim() ? 'true' : 'false');

            const formField = input.closest('mat-form-field');
            if (value.trim()) {
                formField?.classList.add('mat-form-field-should-float');
            }
        }

        if (sourceWrapper) {
            wrapper.dataset['baseFontSize'] = sourceWrapper.dataset['baseFontSize'] ?? '';
            wrapper.dataset['fieldColor'] = sourceWrapper.dataset['fieldColor'] ?? '';
            if (input && type !== 'checkbox') {
                const sourceInput = sourceWrapper.querySelector(
                    type === 'checkbox'
                        ? 'mat-checkbox[data-type="CHECKBOX"] input[type="checkbox"]'
                        : `mat-form-field[data-type="${dataType}"] input`,
                ) as HTMLInputElement | null;
                if (sourceInput?.style.fontSize) {
                    input.style.fontSize = sourceInput.style.fontSize;
                }
                if (sourceInput?.style.color) {
                    input.style.color = sourceInput.style.color;
                }
            }
        }

        if (type === 'text' || type === 'initials') {
            this.syncSelfSignSignTagHostWidth(wrapper);
        } else if (type === 'checkbox') {
            this.syncSelfSignSignTagHostWidth(wrapper);
        }

        const annotation = this.annotations.find((a) => a.element === wrapper);
        if (annotation?.properties && (type === 'text' || type === 'initials' || type === 'date')) {
            annotation.properties['fieldTextValue'] = value;
        }
        if (annotation?.properties && type === 'checkbox' && sourceWrapper) {
            annotation.properties['placeholder'] = this.readSelfSignCheckboxLabel(wrapper);
        }
    }

    private scheduleCloneAnnotationContent(
        source: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        target: { element: HTMLElement; type: string; properties: Record<string, unknown> },
    ): void {
        const needsDeferredClone =
            this.isSelfSignSignTagHost(target.element) &&
            (target.type === 'text' ||
                target.type === 'initials' ||
                target.type === 'date' ||
                target.type === 'checkbox');

        if (needsDeferredClone) {
            this.cloneSelfSignSignTagContentWhenReady(source, target);
            return;
        }

        if (
            this.selfSignMode &&
            (target.type === 'esign' || target.type === 'stamp' || target.type === 'comment')
        ) {
            this.cloneSelfSignMediaContentWhenReady(source, target);
            return;
        }

        this.cloneSelfSignAnnotationContent(source, target);
    }

    private cloneSelfSignMediaContentWhenReady(
        source: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        target: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        attempt = 0,
    ): void {
        const imageBase64 = this.resolveSelfSignAnnotationImageDataUrl({
            element: source.element,
            type: source.type,
            properties: source.properties,
        });

        if (!imageBase64) {
            return;
        }

        const targetContent = this.getAnnotationContentElement(target.element);
        if (!targetContent && attempt < 40) {
            setTimeout(
                () => this.cloneSelfSignMediaContentWhenReady(source, target, attempt + 1),
                20,
            );
            return;
        }

        this.cloneSelfSignAnnotationContent(source, target);

        const wrapper = target.element;
        const cloned =
            target.type !== 'esign' ||
            this.isSelfSignFieldSigned(wrapper) ||
            !!wrapper.querySelector('img[data-value-available="true"]');

        if (!cloned && attempt < 12) {
            setTimeout(
                () => this.cloneSelfSignMediaContentWhenReady(source, target, attempt + 1),
                40 + attempt * 20,
            );
        }
    }

    private finalizeSelfSignPageCopies(
        source: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        targets: Array<{ element: HTMLElement; type: string; properties: Record<string, unknown> }>,
    ): void {
        if (!this.selfSignMode || !targets.length) return;

        const attemptFinalize = (attempt = 0) => {
            for (const target of targets) {
                this.cloneSelfSignAnnotationContent(source, target);
            }

            if (source.type === 'esign') {
                const imageBase64 = this.resolveSelfSignAnnotationImageDataUrl({
                    element: source.element,
                    type: source.type,
                    properties: source.properties,
                });
                if (imageBase64) {
                    for (const target of targets) {
                        if (target.type !== 'esign') continue;
                        const wrapper = target.element;
                        if (this.isSelfSignFieldSigned(wrapper)) continue;
                        if (wrapper.querySelector('img[data-value-available="true"]')) continue;

                        this.applySignatureToAnnotation(wrapper, 'esign', {
                            imageBase64,
                            signatureId: (source.properties['signatureId'] as string) ?? '',
                            signComment: (source.properties['signComment'] as string) ?? '',
                            comment: (source.properties['commentText'] as string) ?? '',
                            sourceImageBase64:
                                (source.properties['sourceImageBase64'] as string) ?? undefined,
                            signatureColor:
                                (source.properties['signatureColor'] as string) ?? undefined,
                            force: true,
                        });
                    }
                }
            }

            const needsRetry = targets.some((target) => {
                if (target.type === 'esign') {
                    const wrapper = target.element;
                    if (this.isSelfSignFieldSigned(wrapper)) return false;
                    if (wrapper.querySelector('img[data-value-available="true"]')) return false;
                    return !!this.resolveSelfSignAnnotationImageDataUrl({
                        element: source.element,
                        type: source.type,
                        properties: source.properties,
                    });
                }

                if (target.type === 'text' || target.type === 'initials' || target.type === 'date') {
                    const expected =
                        (source.properties['fieldTextValue'] ?? '').toString().trim() ||
                        this.readSelfSignTagFieldValue(source.element, target.type);
                    if (!expected) return false;
                    const actual = this.readSelfSignTagFieldValue(target.element, target.type);
                    return actual !== expected;
                }

                return false;
            });

            if (needsRetry && attempt < 12) {
                setTimeout(() => attemptFinalize(attempt + 1), 40 + attempt * 20);
                return;
            }

            this.cdr.detectChanges();
        };

        requestAnimationFrame(() => requestAnimationFrame(() => attemptFinalize(0)));
    }

    private cloneSelfSignSignTagContentWhenReady(
        source: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        target: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        attempt = 0,
    ): void {
        const targetWrapper = target.element;
        if (!this.getSelfSignTagComponent(targetWrapper) && attempt < 30) {
            setTimeout(
                () => this.cloneSelfSignSignTagContentWhenReady(source, target, attempt + 1),
                20,
            );
            return;
        }

        this.cloneSelfSignAnnotationContent(source, target);
    }

    private cloneSelfSignAnnotationContent(
        source: { element: HTMLElement; type: string; properties: Record<string, unknown> },
        target: { element: HTMLElement; type: string; properties: Record<string, unknown> },
    ): void {
        if (source.type !== target.type) return;

        const propsToCopy = [
            'fontSize',
            'placeholder',
            'user',
            'signatureColor',
            'signImgStr',
            'signatureId',
            'signComment',
            'commentText',
            'sourceImageBase64',
            'uploadType',
            'fieldTextValue',
        ] as const;

        for (const key of propsToCopy) {
            if (source.properties[key] !== undefined) {
                target.properties[key] = source.properties[key];
            }
        }

        const sourceWrapper = source.element;
        const targetWrapper = target.element;
        const type = source.type;

        if (this.isSelfSignSignTagHost(sourceWrapper) && this.isSelfSignSignTagHost(targetWrapper)) {
            if (type === 'text' || type === 'initials') {
                let value = this.readSelfSignTagFieldValue(sourceWrapper, type);
                if (!value) {
                    value = (source.properties['fieldTextValue'] ?? '').toString().trim();
                }
                this.writeSelfSignTagFieldValue(targetWrapper, type, value, sourceWrapper);
                return;
            }

            if (type === 'date') {
                let value = this.readSelfSignTagFieldValue(sourceWrapper, 'date');
                if (!value) {
                    value = (source.properties['fieldTextValue'] ?? '').toString().trim();
                }
                this.writeSelfSignTagFieldValue(targetWrapper, 'date', value, sourceWrapper);
                return;
            }

            if (type === 'checkbox') {
                const sourceComponent = this.getSelfSignTagComponent(sourceWrapper);
                const targetComponent = this.getSelfSignTagComponent(targetWrapper);
                if (sourceComponent && targetComponent) {
                    targetComponent.tagValue = sourceComponent.tagValue;
                    targetComponent.checkboxLabelText = sourceComponent.checkboxLabelText;
                    this.cdr.detectChanges();
                } else {
                    const sourceInput = sourceWrapper.querySelector(
                        'mat-checkbox input[type="checkbox"], input[type="checkbox"]',
                    ) as HTMLInputElement | null;
                    const targetInput = targetWrapper.querySelector(
                        'mat-checkbox input[type="checkbox"], input[type="checkbox"]',
                    ) as HTMLInputElement | null;
                    if (sourceInput && targetInput) {
                        targetInput.checked = sourceInput.checked;
                        targetInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    const label = this.readSelfSignCheckboxLabel(sourceWrapper);
                    const targetLabelInput = targetWrapper.querySelector(
                        '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
                    ) as HTMLInputElement | null;
                    const targetSpan = targetWrapper.querySelector('span[contenteditable="true"], span');
                    if (targetLabelInput) {
                        targetLabelInput.value = label;
                    } else if (targetSpan) {
                        targetSpan.textContent = label;
                    }
                }
                this.syncSelfSignSignTagHostWidth(targetWrapper);
            }
            return;
        }

        if (type === 'esign' || type === 'stamp' || type === 'comment') {
            const imageBase64 = this.resolveSelfSignAnnotationImageDataUrl({
                element: sourceWrapper,
                type: source.type,
                properties: source.properties,
            });
            if (!imageBase64) return;

            this.applySignatureToAnnotation(targetWrapper, type, {
                imageBase64,
                signatureId: (source.properties['signatureId'] as string) ?? '',
                signComment: (source.properties['signComment'] as string) ?? '',
                comment: (source.properties['commentText'] as string) ?? '',
                sourceImageBase64: (source.properties['sourceImageBase64'] as string) ?? undefined,
                signatureColor: (source.properties['signatureColor'] as string) ?? undefined,
                force: true,
            });
            return;
        }

        if (type === 'text' || type === 'initials') {
            const sourceContent = this.getAnnotationContentElement(sourceWrapper);
            const targetContent = this.getAnnotationContentElement(targetWrapper);
            if (!sourceContent || !targetContent) return;

            targetContent.textContent = sourceContent.textContent ?? '';
            targetContent.style.fontSize = sourceContent.style.fontSize;
            targetContent.style.color = sourceContent.style.color;
            targetWrapper.dataset['baseFontSize'] = sourceWrapper.dataset['baseFontSize'] ?? '';
            targetWrapper.dataset['fieldColor'] = sourceWrapper.dataset['fieldColor'] ?? '';
            if (type === 'initials') {
                this.syncInitialsWrapperWidth(targetWrapper, targetContent);
            } else {
                this.syncAnnotationWrapperSize(targetWrapper);
            }
            return;
        }

        if (type === 'date') {
            const sourceInput = sourceWrapper.querySelector('input[type="date"]') as HTMLInputElement | null;
            const targetInput = targetWrapper.querySelector('input[type="date"]') as HTMLInputElement | null;
            if (!sourceInput || !targetInput) return;
            targetInput.value = sourceInput.value;
            if (source.properties['fontSize']) {
                targetInput.style.fontSize = `${source.properties['fontSize']}px`;
            }
            return;
        }

        if (type === 'checkbox') {
            const sourceInput = sourceWrapper.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            const targetInput = targetWrapper.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            const sourceLabel = sourceWrapper.querySelector('span');
            const targetLabel = targetWrapper.querySelector('span');
            if (sourceInput && targetInput) {
                targetInput.checked = sourceInput.checked;
            }
            if (sourceLabel && targetLabel) {
                targetLabel.textContent = sourceLabel.textContent ?? '';
            }
        }
    }

    private getAnnotationImageDataUrl(annotation: {
        element: HTMLElement;
        properties?: Record<string, unknown>;
    }): string | null {
        const wrapper = annotation.element;
        const img = wrapper.querySelector('img[data-value-available="true"]') as HTMLImageElement | null;
        if (img?.src?.trim()) {
            return img.src;
        }

        const signImgStr = (annotation.properties?.['signImgStr'] ?? '').toString().trim();
        if (!signImgStr) return null;
        if (signImgStr.startsWith('data:')) return signImgStr;
        return `data:image/png;base64,${signImgStr}`;
    }

    copyAnnotationToPages(option: string): void {
        if (!this.selectedAnnotation) return;

        const currentAnnotation = this.selectedAnnotation;
        this.syncSelfSignFieldStateToProperties(currentAnnotation);

        const annotationType = currentAnnotation.type;
        const currentPage = currentAnnotation.properties.page;

        const pagesToCopy =
            option === 'all'
                ? this.totalPages
                : option === 'even'
                    ? this.totalPages.filter((page) => page % 2 === 0)
                    : option === 'odd'
                        ? this.totalPages.filter((page) => page % 2 !== 0)
                        : [parseInt(option, 10)];

        const pdfPages = Array.from(this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')) as HTMLElement[];
        const currentPageElement = pdfPages[currentPage - 1];
        if (!currentPageElement) return;

        const sourceBounds = this.getAnnotationLocalBounds(
            currentAnnotation.element as HTMLElement,
            currentPageElement,
        );

        const copiedTargets: Array<{
            element: HTMLElement;
            type: string;
            properties: Record<string, unknown>;
        }> = [];

        const sourceSnapshot = {
            element: currentAnnotation.element as HTMLElement,
            type: currentAnnotation.type,
            properties: { ...currentAnnotation.properties } as Record<string, unknown>,
        };

        for (const page of pagesToCopy) {
            if (page !== currentPage) {
                const pageElement = pdfPages[page - 1];
                if (!pageElement) {
                    console.error(`Page ${page} not found.`);
                    continue;
                }

                const beforeCount = this.annotations.length;
                this.addAnnotation(
                    annotationType,
                    sourceBounds.left,
                    sourceBounds.top,
                    page,
                    { width: sourceBounds.width, height: sourceBounds.height },
                );
                const added = this.annotations[beforeCount];
                if (added) {
                    copiedTargets.push({
                        element: added.element as HTMLElement,
                        type: added.type,
                        properties: added.properties as Record<string, unknown>,
                    });
                    this.scheduleCloneAnnotationContent(sourceSnapshot, {
                        element: added.element as HTMLElement,
                        type: added.type,
                        properties: added.properties as Record<string, unknown>,
                    });
                }
            }
        }

        if (copiedTargets.length) {
            this.finalizeSelfSignPageCopies(sourceSnapshot, copiedTargets);
        }
    }

    updateAnnotationProperty(property: string, value: any): void {
        if (this.selectedAnnotation && this.selectedAnnotation.properties) {
            if (property === 'user' && value != null) {
                value = value.toString().trim().toLowerCase();
            }
            this.selectedAnnotation.properties[property] = value;

            const wrapper = this.selectedAnnotation.element as HTMLElement;
            const contentElement = this.getAnnotationContentElement(wrapper);
            if (property === 'fontSize' && contentElement) {
                wrapper.dataset['baseFontSize'] = String(value);
                if (this.isSelfSignSignTagHost(wrapper)) {
                    const dataType = this.selectedAnnotation.type === 'initials'
                        ? 'INITIALS'
                        : this.selectedAnnotation.type === 'date'
                            ? 'DATE'
                            : this.selectedAnnotation.type === 'text'
                                ? 'TEXT'
                                : '';
                    const input = dataType
                        ? wrapper.querySelector(`mat-form-field[data-type="${dataType}"] input`) as HTMLInputElement | null
                        : null;
                    if (input) {
                        input.style.fontSize = `${value}px`;
                    }
                } else {
                    this.syncAnnotationWrapperSize(wrapper);
                    if (this.selectedAnnotation.type === 'initials') {
                        this.syncInitialsWrapperWidth(wrapper, contentElement);
                    }
                }
            } else if (property === 'placeholder' && contentElement) {
                if (this.isSelfSignSignTagHost(wrapper)) {
                    if (this.selectedAnnotation.type === 'checkbox') {
                        const component = this.getSelfSignTagComponent(wrapper);
                        if (component) {
                            component.checkboxLabelText = value;
                        } else {
                            const labelInput = wrapper.querySelector(
                                '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
                            ) as HTMLInputElement | null;
                            if (labelInput) {
                                labelInput.value = value;
                            }
                        }
                        this.syncSelfSignSignTagHostWidth(wrapper);
                    } else if (
                        this.selectedAnnotation.type === 'text'
                        || this.selectedAnnotation.type === 'initials'
                    ) {
                        const label = wrapper.querySelector(
                            `mat-form-field[data-type="${this.selectedAnnotation.type.toUpperCase()}"] mat-label`,
                        ) as HTMLElement | null;
                        if (label) {
                            label.textContent = value;
                        }
                    }
                } else {
                const labeledValue = contentElement.querySelector('.self-sign-tag-field__value') as HTMLElement | null;
                if (this.selectedAnnotation.type === 'text'
                    || this.selectedAnnotation.type === 'esign'
                    || this.selectedAnnotation.type === 'comment'
                    || this.selectedAnnotation.type === 'initials'
                    || this.selectedAnnotation.type === 'stamp'
                ) {
                    if (labeledValue) {
                        labeledValue.textContent = value;
                    } else {
                        contentElement.textContent = value;
                    }
                } else if (this.selectedAnnotation.type === 'checkbox') {
                    const label = contentElement.querySelector('.self-sign-tag-field__checkbox-label')
                        ?? contentElement.querySelector('span[contenteditable="true"]')
                        ?? contentElement.querySelector('span');
                    if (label) label.textContent = value;
                } else if (this.selectedAnnotation.type === 'date') {
                    const input = (contentElement.querySelector('.self-sign-tag-field__date-input')
                        ?? contentElement.querySelector('input')) as HTMLInputElement | null;
                    if (input) input.placeholder = value;
                }
                }
            } else if (
                property === 'signatureColor' &&
                this.selfSignMode &&
                this.selectedAnnotation.type === 'esign'
            ) {
                void this.updateSelfSignSignatureColor(value);
            } else if (
                property === 'signatureColor' &&
                (this.selectedAnnotation.type === 'text'
                    || this.selectedAnnotation.type === 'comment'
                    || this.selectedAnnotation.type === 'initials')
            ) {
                void this.updateSelfSignInlineFieldColor(value);
            }

            // Update the annotations array to maintain properties uniquely
            const annotationIndex = this.annotations.findIndex((a) => a.element === this.selectedAnnotation.element);
            if (annotationIndex !== -1) {
                this.annotations[annotationIndex] = this.selectedAnnotation;
            }
        }
    }

    private getPageNumberForAnnotation(element: HTMLElement): number | null {
        const pages = Array.from(this.pdfContainer.nativeElement.querySelectorAll('.pdf-page')) as HTMLElement[];

        for (let i = 0; i < pages.length; i++) {
            const pageRect = this.getPageReferenceRect(pages[i]);
            const elementRect = element.getBoundingClientRect();

            // Check if the annotation is within the bounds of this page
            if (
                elementRect.top >= pageRect.top &&
                elementRect.bottom <= pageRect.bottom &&
                elementRect.left >= pageRect.left &&
                elementRect.right <= pageRect.right
            ) {
                return i + 1; // Pages are 1-indexed
            }
        }

        return null;
    }

    private resolveDefaultAssigneeEmail(): string {
        const rows = this.tableService.tableInputData ?? [];
        if (!rows.length) return '';

        if (rows.length === 1) {
            return (rows[0].email ?? '').toString().trim().toLowerCase();
        }

        const signer = rows.find(
            (r) =>
                (r.role ?? '').toString().trim().toLowerCase() ===
                EnumSignerRoleType.SIGNER.toLowerCase(),
        );
        return (signer?.email ?? rows[0]?.email ?? '').toString().trim().toLowerCase();
    }

    private resolveSelfSignAssigneeEmail(): string {
        const fromTable = this.resolveDefaultAssigneeEmail();
        if (fromTable) return fromTable;
        return (this._authService.getUserDetails()?.Email ?? '').toString().trim().toLowerCase();
    }

    private ensureSelfSignAssigneesReady(): boolean {
        if (!this.selfSignMode) {
            const unassigned = this.annotations.filter(
                (a) => !(a.properties?.user ?? '').toString().trim(),
            );
            if (unassigned.length > 0) {
                this._toastr.showError('Please assign each field to a signer in the Property panel');
                return false;
            }
            return true;
        }

        const assigneeEmail = this.resolveSelfSignAssigneeEmail();
        if (!assigneeEmail) {
            this._toastr.showError('Unable to resolve signer email for self sign.');
            return false;
        }

        for (const ann of this.annotations) {
            if (!(ann.properties?.user ?? '').toString().trim()) {
                ann.properties.user = assigneeEmail;
            } else {
                ann.properties.user = ann.properties.user.toString().trim().toLowerCase();
            }
        }
        return true;
    }

    private ensureAnnotationAssignees(): void {
        const defaultEmail = this.resolveDefaultAssigneeEmail();
        if (!defaultEmail) return;

        for (const ann of this.annotations) {
            if (!(ann.properties?.user ?? '').toString().trim()) {
                ann.properties.user = defaultEmail;
            } else {
                ann.properties.user = ann.properties.user.toString().trim().toLowerCase();
            }
        }
    }

    private mapAnnotationTypeToApi(type: string): string {
        const map: Record<string, EnumSignTagTypes> = {
            esign: EnumSignTagTypes.SIGNATURE,
            initials: EnumSignTagTypes.INITIALS,
            stamp: EnumSignTagTypes.STAMP,
            text: EnumSignTagTypes.TEXT,
            date: EnumSignTagTypes.DATE,
            checkbox: EnumSignTagTypes.CHECKBOX,
            comment: EnumSignTagTypes.COMMENT,
        };
        return map[type.toLowerCase()] ?? type;
    }

    /** Sign/Upload DOM + payload uses SIGNATURE; create-request Tags use Esign. */
    private mapUploadDataType(type: string): string {
        return this._signService.normalizeUploadSignatureType(this.mapAnnotationTypeToApi(type));
    }

    onFieldStepBack(): void {
        this._createFlow.triggerFieldStepBack();
    }

    onFieldStepSubmit(): void {
        this._createFlow.triggerFieldStepSubmit();
    }

    goToPrevious(): void {
        this.previousStep.emit(); // Emit an event to the parent
    }

    protected readonly HTMLInputElement = HTMLInputElement;
}
